import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { adminApi } from '../../utils/admin-api';
import { sessionStorage as authSessionStorage } from '../../utils/auth-api';
import { generateCustomPLCReport } from '../../utils/advanced-pdf-generator';
import { API_BASE } from '../../utils/api-base';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { Download, RefreshCcw, Database, Activity, Calendar, FileText } from 'lucide-react';

interface HistoricalDataProps {
  clientId: string;
  token: string;
  permissions?: any;
}

type IntervalUnit = 'seconds' | 'minutes' | 'hours';

const MIN_REPORT_SECONDS_INTERVAL = 2;

const normalizeIntervalValue = (value: number, unit: IntervalUnit): number => {
  const safeValue = Math.max(0, Math.floor(Number(value) || 0));
  if (safeValue <= 0) return 0;
  if (unit === 'seconds') return Math.max(MIN_REPORT_SECONDS_INTERVAL, safeValue);
  return safeValue;
};

const toIntervalParam = (value: number, unit: IntervalUnit): string => {
  const safeValue = normalizeIntervalValue(value, unit);
  if (safeValue <= 0) return '0';
  const suffix = unit === 'seconds' ? 's' : unit === 'hours' ? 'h' : 'm';
  return `${safeValue}${suffix}`;
};

const parseIntervalConfig = (config: any): string => {
  if (!config || typeof config !== 'object') return '0';

  const unitFromConfig =
    config.intervalUnit === 'seconds' || config.intervalUnit === 'hours' || config.intervalUnit === 'minutes'
      ? config.intervalUnit
      : config.reportIntervalUnit === 'seconds' || config.reportIntervalUnit === 'hours' || config.reportIntervalUnit === 'minutes'
        ? config.reportIntervalUnit
        : 'minutes';

  const valueCandidate = Number(config.intervalValue ?? config.reportIntervalValue);
  if (Number.isFinite(valueCandidate) && valueCandidate > 0) {
    return toIntervalParam(valueCandidate, unitFromConfig);
  }

  const legacy = config.interval;
  if (typeof legacy === 'number' && Number.isFinite(legacy) && legacy > 0) {
    return toIntervalParam(legacy, 'minutes');
  }
  if (typeof legacy === 'string' && legacy.trim()) {
    const raw = legacy.trim().toLowerCase();
    if (/^\d+$/.test(raw)) return toIntervalParam(Number(raw), 'minutes');
    if (/^\d+\s*[smh]$/.test(raw)) return raw.replace(/\s+/g, '');
  }

  return '0';
};

const formatIntervalLabel = (intervalParam: string): string => {
  const normalized = String(intervalParam || '0').trim().toLowerCase();
  if (!normalized || normalized === '0') return 'Raw data';
  const match = normalized.match(/^(\d+)([smh])$/);
  if (!match) return 'Raw data';
  const value = Number(match[1]);
  const unit = match[2] === 's' ? 'second' : match[2] === 'h' ? 'hour' : 'minute';
  return `${value} ${unit}${value === 1 ? '' : 's'}`;
};

const parseIntervalParam = (intervalParam: string): { value: number; unit: IntervalUnit } => {
  const normalized = String(intervalParam || '0').trim().toLowerCase();
  const match = normalized.match(/^(\d+)([smh])$/);
  if (!match) {
    return { value: 0, unit: 'minutes' };
  }

  return {
    value: normalizeIntervalValue(
      Number(match[1]) || 0,
      match[2] === 's' ? 'seconds' : match[2] === 'h' ? 'hours' : 'minutes'
    ),
    unit: match[2] === 's' ? 'seconds' : match[2] === 'h' ? 'hours' : 'minutes',
  };
};

export function HistoricalData({ clientId, token, permissions }: HistoricalDataProps) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  
  // Selectors State
  const [deviceType, setDeviceType] = useState<'plc' | 'energy_meter' | 'scanner' | 'summary'>(() => {
    if (permissions?.livePlcData?.view !== false) return 'plc';
    if (permissions?.energyConsumption?.view !== false) return 'energy_meter';
    if (permissions?.scanners?.view !== false) return 'scanner';
    return 'plc';
  });

  // If access to Summarise Report is revoked while it's the active tab, fall back to an allowed source.
  useEffect(() => {
    if (deviceType === 'summary' && permissions?.reports?.summaryReport !== true) {
      if (permissions?.livePlcData?.view !== false) setDeviceType('plc');
      else if (permissions?.energyConsumption?.view !== false) setDeviceType('energy_meter');
      else if (permissions?.scanners?.view !== false) setDeviceType('scanner');
    }
  }, [deviceType, permissions]);

  const [plcs, setPlcs] = useState<any[]>([]);
  const [selectedPlc, setSelectedPlc] = useState<string>('');
  const [tags, setTags] = useState<any[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  
  const [meters, setMeters] = useState<any[]>([]);
  const [selectedMeter, setSelectedMeter] = useState<string>('');
  const [exportAllMeters, setExportAllMeters] = useState<boolean>(true);
  const [selectedExportMeterIds, setSelectedExportMeterIds] = useState<string[]>([]);
  const [selectedMeterTagKeys, setSelectedMeterTagKeys] = useState<string[]>([]);

  const [scanners, setScanners] = useState<any[]>([]);
  const [selectedScanner, setSelectedScanner] = useState<string>('');

  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [activeTagNames, setActiveTagNames] = useState<string[]>([]);
  const [summaryStats, setSummaryStats] = useState<{ avgVoltage: number; minVoltage: number; maxVoltage: number; avgActiveKw: number; minActiveKw: number; maxActiveKw: number; totalConsumption: number; peakKw: number; meterCount: number } | null>(null);
  const [summaryTableRows, setSummaryTableRows] = useState<any[]>([]);
  const [summaryTrendData, setSummaryTrendData] = useState<{ label: string; avgVoltage: number; avgActiveKw: number; kwh: number; cumulativeKwh: number }[]>([]);
  const [summaryMeterCounts, setSummaryMeterCounts] = useState<{ total: number; active: number; inactive: number }>({ total: 0, active: 0, inactive: 0 });
  const [adminDefaultInterval, setAdminDefaultInterval] = useState<string>('0');

  const plcTemplates = useMemo(() => {
    return templates.filter((template: any) => {
      const moduleName = String(template?.module || '').trim().toLowerCase();
      if (moduleName === 'plc' || moduleName === 'liveplcdata' || moduleName === 'live_plc_data' || moduleName === 'liveplc') {
        return true;
      }
      return !moduleName && Array.isArray(template?.config?.tagIds) && template.config.tagIds.length > 0;
    });
  }, [templates]);

  // Section Filtering
  const [sections, setSections] = useState<any[]>([]);
  const [selectedSection, setSelectedSection] = useState<string>('all');
  const [intervalValue, setIntervalValue] = useState<number>(0);
  const [intervalUnit, setIntervalUnit] = useState<IntervalUnit>('minutes');
  const [intervalCustomized, setIntervalCustomized] = useState(false);

  const intervalParam = useMemo(() => {
    if (!Number.isFinite(intervalValue) || intervalValue <= 0) return '0';
    return toIntervalParam(intervalValue, intervalUnit);
  }, [intervalValue, intervalUnit]);

  const filteredTags = useMemo(() => {
    if (selectedSection === 'all') {
      return tags;
    }
    return tags.filter((tag: any) => String(tag?.sectionId ?? tag?.section_id ?? '') === selectedSection);
  }, [tags, selectedSection]);

  const effectiveTagIds = useMemo(() => {
    if (selectedTagIds.length > 0) {
      return selectedTagIds;
    }
    if (selectedSection !== 'all') {
      return filteredTags.map((tag: any) => String(tag.id)).filter(Boolean);
    }
    return [];
  }, [selectedTagIds, selectedSection, filteredTags]);

  const normalizeTagToken = (value: any) => String(value ?? '').trim().toLowerCase();
  const slugifyTagToken = (value: any) => normalizeTagToken(value).replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'tag';
  const buildMeterTagSelectionKey = (meterId: string, tagName: any) => `${String(meterId)}::${normalizeTagToken(tagName)}`;

  const selectedExportMeterIdSet = useMemo(() => {
    if (exportAllMeters) {
      return new Set(meters.map((meter: any) => String(meter.id)).filter(Boolean));
    }
    return new Set(selectedExportMeterIds.map((id) => String(id)).filter(Boolean));
  }, [exportAllMeters, meters, selectedExportMeterIds]);

  const energyMeterTagOptions = useMemo(() => {
    const options: Array<{ key: string; meterId: string; meterName: string; tagName: string; unit: string }> = [];
    const seen = new Set<string>();

    meters.forEach((meter: any) => {
      const meterId = String(meter?.id || '');
      if (!meterId || !selectedExportMeterIdSet.has(meterId)) return;
      const meterName = String(meter?.name || meterId);
      const meterTags = Array.isArray(meter?.tags) ? meter.tags : [];

      meterTags.forEach((tag: any) => {
        const tagName = String(tag?.name || tag?.tagName || '').trim();
        if (!tagName) return;
        const key = buildMeterTagSelectionKey(meterId, tagName);
        if (seen.has(key)) return;
        seen.add(key);
        options.push({
          key,
          meterId,
          meterName,
          tagName,
          unit: String(tag?.unit || '').trim(),
        });
      });
    });

    return options.sort((a, b) => {
      const meterCompare = a.meterName.localeCompare(b.meterName);
      if (meterCompare !== 0) return meterCompare;
      return a.tagName.localeCompare(b.tagName);
    });
  }, [meters, selectedExportMeterIdSet]);

  const shouldIncludeEnergyMeterTag = (meterId: string, tagName: any) => {
    if (selectedMeterTagKeys.length === 0) return true;
    if (!tagName) return false;
    const key = buildMeterTagSelectionKey(meterId, tagName);
    return selectedMeterTagKeys.includes(key);
  };

  const isActiveKwTag = (name: string, unit: string) => {
    const normalizedName = String(name || '').toLowerCase();
    const normalizedUnit = String(unit || '').toLowerCase();
    return (normalizedUnit === 'kw' || normalizedName.includes('active')) && !normalizedName.includes('kwh');
  };

  const isVoltageTag = (name: string, unit: string) => {
    const normalizedName = String(name || '').toLowerCase();
    const normalizedUnit = String(unit || '').toLowerCase();
    return normalizedUnit === 'v' || normalizedName.includes('volt');
  };

  const isKwhTag = (unit: string) => {
    const normalizedUnit = String(unit || '').toLowerCase();
    return normalizedUnit === 'kwh';
  };

  // Date + Time Filters
  const toLocalDateStr = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const [dateRange, setDateRange] = useState<string>('7d');
  const [customStart, setCustomStart] = useState<string>(
    toLocalDateStr(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000))
  );
  const [customEnd, setCustomEnd] = useState<string>(
    toLocalDateStr(new Date())
  );
  const [customStartTime, setCustomStartTime] = useState<string>('00:00');
  const [customEndTime, setCustomEndTime] = useState<string>('23:59');

  const canDownload = deviceType === 'plc'
    ? !!permissions?.livePlcData?.download
    : deviceType === 'scanner'
    ? !!permissions?.scanners?.download
    : deviceType === 'energy_meter' || deviceType === 'summary'
    ? !!permissions?.energyConsumption?.download
    : false;

  useEffect(() => {
    loadDevices();
    loadTemplates();
  }, [clientId, deviceType]);

  const loadTemplates = async () => {
    try {
      const [allTemplates, pdfTemplate] = await Promise.all([
        adminApi.getReportTemplates(token, clientId),
        adminApi.getPDFTemplate(token, clientId).catch(() => ({} as any)),
      ]);
      // Only keep active templates
      setTemplates(allTemplates.filter((t: any) => (t.isActive ?? t.is_active ?? true)));
      setAdminDefaultInterval(parseIntervalConfig(pdfTemplate?.advancedTemplate || {}));
    } catch (e) {
      console.error('Failed to load templates:', e);
    }
  };

  useEffect(() => {
    if (deviceType !== 'plc' || intervalCustomized) return;

    const selected = plcTemplates.find((t: any) => t.id === selectedTemplate);
    const fallbackInterval = adminDefaultInterval || '0';
    if (!selected) {
      const parsed = parseIntervalParam(fallbackInterval);
      setIntervalValue(parsed.value);
      setIntervalUnit(parsed.unit);
      return;
    }

    const templateInterval = parseIntervalConfig(selected.config || {});
    const resolvedInterval = templateInterval !== '0' ? templateInterval : fallbackInterval;
    const parsed = parseIntervalParam(resolvedInterval);
    setIntervalValue(parsed.value);
    setIntervalUnit(parsed.unit);
  }, [deviceType, selectedTemplate, plcTemplates, adminDefaultInterval, intervalCustomized]);

  const loadDevices = async () => {
    try {
      if (deviceType === 'plc') {
        const plcList = await adminApi.getPLCs(token, clientId);
        setPlcs(plcList);
        if (plcList.length > 0 && !selectedPlc) {
          setSelectedPlc(plcList[0].id);
        }
      } else if (deviceType === 'energy_meter' || deviceType === 'summary') {
        const response = await fetch(`${API_BASE}/api/clients/${clientId}/energy-meters`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.error || 'Failed to load energy meters');
        }
        const meterList = payload.meters || [];
        setMeters(meterList);
        if (meterList.length > 0 && !selectedMeter) {
          setSelectedMeter(meterList[0].id);
        }
        if (meterList.length > 0 && selectedExportMeterIds.length === 0) {
          setSelectedExportMeterIds(meterList.map((m: any) => String(m.id)));
        }
      } else if (deviceType === 'scanner') {
        const response = await fetch(`${API_BASE}/api/clients/${clientId}/scanners`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.error || 'Failed to load scanners');
        }
        const scannerList = payload.scanners || [];
        setScanners(scannerList);
        if (scannerList.length > 0 && !selectedScanner) {
          setSelectedScanner(scannerList[0].id);
        }
      }
    } catch (error) {
      toast.error('Failed to load devices');
    }
  };

  useEffect(() => {
    if (deviceType === 'plc' && selectedPlc) {
      // Load tags — Reports only deals in analog trend data, so boolean/digital
      // (on-off) tags are excluded here at the source: they never appear in the
      // tag picker below and can never end up in a PDF/Excel export either.
      adminApi.getTags(token, clientId, selectedPlc).then((t) => {
        const floatTags = (Array.isArray(t) ? t : []).filter(
          (tag: any) => String(tag?.dataType ?? tag?.data_type ?? '').toLowerCase() === 'float'
        );
        setTags(floatTags);
        setSelectedTagIds([]);
      }).catch(() => toast.error('Failed to load PLC tags'));

      // Load sections
      fetch(`${API_BASE}/api/clients/${clientId}/plcs/${selectedPlc}/sections`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(res => res.json())
      .then(data => {
        setSections(Array.isArray(data) ? data : data.sections || []);
        setSelectedSection('all');
      })
      .catch(err => console.error('Failed to load sections:', err));
    } else if (deviceType === 'plc') {
      setTags([]);
      setSections([]);
      setSelectedTagIds([]);
      setSelectedSection('all');
    }
  }, [selectedPlc, deviceType, token, clientId]);

  useEffect(() => {
    const validIds = new Set(filteredTags.map((tag: any) => String(tag.id)));
    setSelectedTagIds((previous) => previous.filter((id) => validIds.has(String(id))));
  }, [filteredTags]);

  useEffect(() => {
    const validIds = new Set(meters.map((m) => String(m.id)));
    setSelectedExportMeterIds((prev) => prev.filter((id) => validIds.has(id)));
  }, [meters]);

  useEffect(() => {
    const validKeys = new Set(energyMeterTagOptions.map((option) => option.key));
    setSelectedMeterTagKeys((prev) => prev.filter((key) => validKeys.has(key)));
  }, [energyMeterTagOptions]);

  const getDateRangeParams = () => {
    const [sh, sm] = customStartTime.split(':').map(Number);
    const [eh, em] = customEndTime.split(':').map(Number);
    const s = new Date(`${customStart}T00:00:00`);
    s.setHours(sh, sm, 0, 0);
    const e = new Date(`${customEnd}T00:00:00`);
    e.setHours(eh, em, 59, 999);
    
    // Auto-clamp future end dates to the current time 
    // so "Today" selection doesn't show 23:59:59 on the downloaded report.
    if (e.getTime() > Date.now()) {
      return { startDate: s.toISOString(), endDate: new Date().toISOString() };
    }
    
    return { startDate: s.toISOString(), endDate: e.toISOString() };
  };

  const validateDateRange = () => {
    if (!customStart || !customEnd || !customStartTime || !customEndTime) {
      throw new Error('Please select both start and end date/time');
    }

    const { startDate, endDate } = getDateRangeParams();
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      throw new Error('Invalid date range values');
    }
    if (start > end) {
      throw new Error('Start date/time must be earlier than end date/time');
    }

    return { startDate, endDate };
  };

  const fetchEnergyMeterReadings = async (meter: any, startDate: string, endDate: string, limit = '10000', offset = '0') => {
    const query = new URLSearchParams({
      limit,
      offset,
      startDate,
      endDate,
    });
    if (intervalParam && intervalParam !== '0') {
      query.set('interval', intervalParam);
    }

    const resp = await fetch(`${API_BASE}/api/clients/${clientId}/energy-meters/${meter.id}/readings?${query.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const payload = await resp.json();
    if (!resp.ok) {
      throw new Error(payload?.error || `Failed to fetch readings for meter ${meter.name || meter.id}`);
    }

    return {
      meterId: meter.id,
      meterName: payload?.meter?.name || meter?.name || meter.id || 'Energy Meter',
      tags: Array.isArray(payload?.tags) ? payload.tags : (Array.isArray(meter.tags) ? meter.tags : []),
      readings: Array.isArray(payload?.readings) ? payload.readings : [],
      totalReadings: Number(payload?.totalReadings || 0),
    };
  };

  const fetchAllEnergyMeterReadings = async (meter: any, startDate: string, endDate: string, pageSize = 10000) => {
    const allReadings: any[] = [];
    let offset = 0;
    let totalReadings = Number.POSITIVE_INFINITY;
    let meterName = meter?.name || meter?.id || 'Energy Meter';
    let tags = Array.isArray(meter?.tags) ? meter.tags : [];

    for (let page = 0; page < 200 && offset < totalReadings; page += 1) {
      const batch = await fetchEnergyMeterReadings(meter, startDate, endDate, String(pageSize), String(offset));
      meterName = batch?.meterName || meterName;
      tags = Array.isArray(batch?.tags) ? batch.tags : tags;

      const rows = Array.isArray(batch?.readings) ? batch.readings : [];
      allReadings.push(...rows);

      const reportedTotal = Number(batch?.totalReadings);
      if (Number.isFinite(reportedTotal) && reportedTotal >= 0) {
        totalReadings = reportedTotal;
      } else {
        totalReadings = offset + rows.length;
      }

      if (rows.length === 0) {
        break;
      }

      offset += pageSize;
    }

    return {
      meterId: meter.id,
      meterName,
      tags,
      readings: allReadings,
    };
  };

  const getMetersForExport = async (): Promise<any[]> => {
    let meterList = meters;

    if (!meterList || meterList.length === 0) {
      const meterResp = await fetch(`${API_BASE}/api/clients/${clientId}/energy-meters`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const meterPayload = await meterResp.json();
      if (!meterResp.ok) {
        throw new Error(meterPayload?.error || 'Failed to load energy meters');
      }
      meterList = meterPayload.meters || [];
    }

    if (!meterList || meterList.length === 0) {
      throw new Error('No energy meters available for export');
    }

    if (exportAllMeters) {
      return meterList;
    }

    const selected = meterList.filter((m: any) => selectedExportMeterIds.includes(String(m.id)));
    if (selected.length === 0) {
      throw new Error('Please select at least one meter for export');
    }

    return selected;
  };

  const downloadEnergyMeterExcel = async (startDate: string, endDate: string) => {
    const metersForExport = await getMetersForExport();
    const meterReadings = await Promise.all(
      metersForExport.map((meter: any) => fetchAllEnergyMeterReadings(meter, startDate, endDate, 10000))
    );

    const rowsByKey = new Map<string, any>();
    const dynamicColumns: string[] = [];
    const seenColumns = new Set<string>();
    const includeMeterInHeader = metersForExport.length > 1;

    const toDatePart = (timestamp: string) => {
      const date = new Date(timestamp);
      if (Number.isNaN(date.getTime())) return '';
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };

    const toTimePart = (timestamp: string) => {
      const date = new Date(timestamp);
      if (Number.isNaN(date.getTime())) return '';
      const h = String(date.getHours()).padStart(2, '0');
      const m = String(date.getMinutes()).padStart(2, '0');
      const s = String(date.getSeconds()).padStart(2, '0');
      return `${h}:${m}:${s}`;
    };

    meterReadings.forEach((meterData: any) => {
      const meterName = String(meterData.meterName || meterData.meterId || 'Energy Meter');
      (meterData.readings || [])
        .filter((reading: any) => shouldIncludeEnergyMeterTag(String(meterData.meterId), reading?.tagName || reading?.tagId || reading?.tag_id))
        .forEach((reading: any) => {
          const timestamp = String(reading?.timestamp || '').trim();
          const tagName = String(reading?.tagName || reading?.tagId || reading?.tag_id || '').trim();
          if (!timestamp || !tagName) return;

          const date = new Date(timestamp);
          if (Number.isNaN(date.getTime())) return;

          const key = `${date.toISOString().slice(0, 19)}__${meterName}`;
          if (!rowsByKey.has(key)) {
            rowsByKey.set(key, {
              Date: toDatePart(timestamp),
              Time: toTimePart(timestamp),
              Meter: meterName,
              __epoch: date.getTime(),
            });
          }

          const unit = String(reading?.unit || '').trim();
          const baseLabel = unit ? `${tagName} (${unit})` : tagName;
          const columnLabel = includeMeterInHeader ? `${meterName} - ${baseLabel}` : baseLabel;

          if (!seenColumns.has(columnLabel)) {
            seenColumns.add(columnLabel);
            dynamicColumns.push(columnLabel);
          }

          const numericValue = Number(reading?.value);
          rowsByKey.get(key)[columnLabel] = Number.isFinite(numericValue) ? numericValue : reading?.value;
        });
    });

    const excelRows = Array.from(rowsByKey.values())
      .sort((a: any, b: any) => {
        const byTime = (a.__epoch || 0) - (b.__epoch || 0);
        if (byTime !== 0) return byTime;
        return String(a.Meter || '').localeCompare(String(b.Meter || ''));
      })
      .map(({ __epoch, ...row }) => row);

    const workbook = XLSX.utils.book_new();
    const headers = ['Date', 'Time', 'Meter', ...dynamicColumns];
    const worksheet = XLSX.utils.json_to_sheet(excelRows, { header: headers });
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Energy Meter Report');

    const excelArray = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
    const blob = new Blob([excelArray], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `energy_meter_report_${Date.now()}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return excelRows.length;
  };

  const downloadEnergyMeterPdf = async (startDate: string, endDate: string) => {
    const toSecondIso = (timestamp: string): string | null => {
      const date = new Date(timestamp);
      if (Number.isNaN(date.getTime())) return null;
      return `${date.toISOString().slice(0, 19)}Z`;
    };

    const meterList = await getMetersForExport();

    const meterReadings = await Promise.all(
      meterList.map((meter: any) => fetchAllEnergyMeterReadings(meter, startDate, endDate, 10000))
    );

    const rowMap = new Map<string, any>();

    type MeterColumn = {
      id: string;
      label: string;
      dataKey: string;
      unit: string;
      format: 'kw' | 'kwh' | 'number';
      order: number;
    };
    type MeterSection = {
      id: string;
      title: string;
      columns: MeterColumn[];
      byToken: Map<string, MeterColumn>;
    };

    const sectionsByMeter = new Map<string, MeterSection>();
    const ensureMeterSection = (meterId: string, meterName: string) => {
      if (!sectionsByMeter.has(meterId)) {
        sectionsByMeter.set(meterId, {
          id: `meter_${meterId}`,
          title: meterName,
          columns: [],
          byToken: new Map<string, MeterColumn>(),
        });
      }
      return sectionsByMeter.get(meterId)!;
    };

    const createOrGetColumn = (
      meterId: string,
      meterName: string,
      tagNameRaw: any,
      unitRaw: any,
      orderHint = 9999
    ) => {
      const token = normalizeTagToken(tagNameRaw);
      if (!token) return null;

      const section = ensureMeterSection(meterId, meterName);
      if (section.byToken.has(token)) {
        const existing = section.byToken.get(token)!;
        if (!existing.unit && unitRaw) {
          existing.unit = String(unitRaw || '').trim();
        }
        return section.byToken.get(token)!;
      }

      const unit = String(unitRaw || '').trim();
      const normalizedUnit = unit.toLowerCase();
      const format: 'kw' | 'kwh' | 'number' = normalizedUnit === 'kwh'
        ? 'kwh'
        : (normalizedUnit === 'kw' ? 'kw' : 'number');

      const safeToken = slugifyTagToken(`${orderHint}_${token}`);
      const column: MeterColumn = {
        id: `${meterId}_${safeToken}`,
        label: String(tagNameRaw || 'TAG').toUpperCase(),
        dataKey: `${meterId}__${safeToken}`,
        unit,
        format,
        order: orderHint,
      };

      section.byToken.set(token, column);
      section.columns.push(column);
      return column;
    };

    meterReadings.forEach((meterData) => {
      const meterId = String(meterData.meterId);
      const meterName = String(meterData.meterName || meterId);
      const configuredTags = Array.isArray(meterData.tags) ? meterData.tags : [];

      configuredTags.forEach((tag: any, index: number) => {
        const tagName = tag?.name || tag?.tagName || tag?.id || tag?.address;
        if (!shouldIncludeEnergyMeterTag(meterId, tagName)) return;
        createOrGetColumn(meterId, meterName, tagName, tag?.unit || '', index);
      });

      (meterData.readings || []).forEach((reading: any, index: number) => {
        const tagName = reading?.tagName || reading?.tagId || reading?.tag_id;
        if (!shouldIncludeEnergyMeterTag(meterId, tagName)) return;

        const column = createOrGetColumn(
          meterId,
          meterName,
          tagName || `TAG_${index + 1}`,
          reading?.unit || '',
          configuredTags.length + index
        );
        if (!column) return;

        const tsKey = toSecondIso(String(reading?.timestamp || ''));
        if (!tsKey) return;

        const value = Number(reading?.value);
        if (!Number.isFinite(value)) return;

        let row = rowMap.get(tsKey);
        if (!row) {
          row = { timestamp: tsKey };
          rowMap.set(tsKey, row);
        }

        row[column.dataKey] = value;
      });
    });

    const reportRows = Array.from(rowMap.values())
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    const dynamicSections = meterList
      .map((meter: any) => sectionsByMeter.get(String(meter.id)))
      .filter((section): section is MeterSection => section !== undefined && section.columns.length > 0)
      .map((section) => ({
        id: section.id,
        title: section.title,
        columns: section.columns
          .sort((a, b) => a.order - b.order)
          .map(({ id, label, dataKey, unit, format }) => ({ id, label, dataKey, unit, format })),
      }));

    if (dynamicSections.length === 0) {
      throw new Error('No matching meter tags found for selected export filters');
    }

    let fetchedTemplate: any = {};
    try {
      fetchedTemplate = await adminApi.getPDFTemplate(token, clientId);
    } catch {
      fetchedTemplate = {};
    }

    const advancedTemplate = fetchedTemplate?.advancedTemplate || {};

    const template = {
      reportTitle: advancedTemplate?.reportTitle || 'ENERGY METER DATA LOG REPORT',
      reportSubtitle: advancedTemplate?.reportSubtitle || '',
      leftLogo: advancedTemplate?.leftLogo,
      rightLogo: advancedTemplate?.rightLogo,
      companyFields: [
        { label: 'COMPANY NAME', value: '{companyName}' },
        { label: 'USER NAME', value: '{downloadedBy}' },
      ],
      showDateRange: advancedTemplate?.showDateRange !== false,
      dateRangeLabel: 'Report Period',
      sections: dynamicSections,
      showCheckedBy: advancedTemplate?.showCheckedBy !== false,
      showVerifiedBy: advancedTemplate?.showVerifiedBy !== false,
      showGeneratedBy: advancedTemplate?.showGeneratedBy !== false,
      footerText: advancedTemplate?.footerText || '',
      includeCharts: advancedTemplate?.includeCharts ?? false,
      primaryColor: advancedTemplate?.primaryColor || '#000000',
      borderStyle: advancedTemplate?.borderStyle || 'single',
      fontSize: advancedTemplate?.fontSize || 'small',
    };

    const authSession = authSessionStorage.get();
    const clientName =
      authSession?.client?.name
      || authSession?.clientName
      || meterList[0]?.clientName
      || 'Client';
    const downloadedBy =
      authSession?.user?.name
      || authSession?.name
      || authSession?.user?.email
      || 'User';

    const reportedMeterName = meterList.length === 1
      ? String(meterList[0]?.name || meterList[0]?.meterName || meterList[0]?.id || '')
      : meterList.length > 1
        ? `${meterList.length} Meters`
        : '';

    await generateCustomPLCReport(
      [],
      reportRows,
      template,
      clientName,
      new Date(startDate),
      new Date(endDate),
      {
        companyName: clientName,
        downloadedBy,
        plcName: reportedMeterName || undefined,
        plcLabel: 'METER',
      }
    );

    return reportRows.length;
  };

  const downloadSummaryExcel = async (startDate: string, endDate: string) => {
    if (!summaryStats || summaryTableRows.length === 0) {
      toast.warning('Please fetch the summary report before exporting');
      return 0;
    }

    const metrics = [
      { Metric: 'Average Voltage', Value: `${summaryStats.avgVoltage.toFixed(2)} V` },
      { Metric: 'Voltage Range', Value: `${summaryStats.minVoltage.toFixed(2)} V - ${summaryStats.maxVoltage.toFixed(2)} V` },
      { Metric: 'Average Active Power', Value: `${summaryStats.avgActiveKw.toFixed(2)} kW` },
      { Metric: 'Active Power Range', Value: `${summaryStats.minActiveKw.toFixed(2)} kW - ${summaryStats.maxActiveKw.toFixed(2)} kW` },
      { Metric: 'Total Consumption', Value: `${summaryStats.totalConsumption.toFixed(2)} kWh` },
      { Metric: 'Peak Active Power', Value: `${summaryStats.peakKw.toFixed(2)} kW` },
      { Metric: 'Total Meters', Value: String(summaryMeterCounts.total) },
      { Metric: 'Active Meters', Value: String(summaryMeterCounts.active) },
      { Metric: 'Inactive Meters', Value: String(summaryMeterCounts.inactive) },
    ];

    const meterRows = summaryTableRows.map((row) => ({
      Meter_Name: row.meterName,
      Avg_Voltage: row.avgVoltage,
      Min_Voltage: row.minVoltage,
      Max_Voltage: row.maxVoltage,
      Avg_Active_kW: row.avgActiveKw,
      Min_Active_kW: row.minActiveKw,
      Max_Active_kW: row.maxActiveKw,
      Total_kWh: row.totalConsumption,
      Percent_of_Total: `${row.percentOfTotal.toFixed(2)}%`,
    }));

    const trendRows = summaryTrendData.map((point) => ({
      Period: point.label,
      Avg_Voltage: point.avgVoltage,
      Avg_Active_kW: point.avgActiveKw,
      Energy_kWh: point.kwh,
      Cumulative_Energy_kWh: point.cumulativeKwh,
    }));

    const workbook = XLSX.utils.book_new();
    const summarySheet = XLSX.utils.json_to_sheet(metrics);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary Metrics');
    const meterSheet = XLSX.utils.json_to_sheet(meterRows);
    XLSX.utils.book_append_sheet(workbook, meterSheet, 'Meter Summary');
    if (trendRows.length > 0) {
      const trendSheet = XLSX.utils.json_to_sheet(trendRows);
      XLSX.utils.book_append_sheet(workbook, trendSheet, 'Trend Data');
    }

    const excelArray = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
    const blob = new Blob([excelArray], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `energy_meter_summary_${Date.now()}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return metrics.length + meterRows.length;
  };

  const createSummaryPdf = async (
    startDate: string,
    endDate: string,
    meterRows: any[],
    trendData: { label: string; avgVoltage: number; avgActiveKw: number; kwh: number; cumulativeKwh: number }[],
    meterCounts: { total: number; active: number; inactive: number }
  ) => {
    if (!summaryStats) return 0;

    const authSession = authSessionStorage.get();
    const clientName = authSession?.client?.name || authSession?.clientName || 'Client';
    const downloadedBy = authSession?.user?.name || authSession?.name || authSession?.user?.email || 'User';

    let fetchedTemplate: any = {};
    try { fetchedTemplate = await adminApi.getPDFTemplate(token, clientId); } catch { fetchedTemplate = {}; }
    const advancedTemplate = fetchedTemplate?.advancedTemplate || {};

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    let y = 18;

    const fmtDateTime = (v: string) => {
      const d = new Date(v);
      return Number.isFinite(d.getTime()) ? d.toLocaleString() : v;
    };

    // ---------- Palette ----------
    const hexToRgb = (hex: string): [number, number, number] => {
      const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
      return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [30, 58, 138];
    };
    const primary = hexToRgb(advancedTemplate?.primaryColor || '#1e3a8a');
    const ink: [number, number, number] = [15, 23, 42];
    const slate: [number, number, number] = [71, 85, 105];
    const muted: [number, number, number] = [100, 116, 139];
    const border: [number, number, number] = [203, 213, 225];
    const faint: [number, number, number] = [226, 232, 240];
    const panelBg: [number, number, number] = [248, 250, 252];
    const blue: [number, number, number] = [59, 130, 246];
    const green: [number, number, number] = [16, 185, 129];
    const amber: [number, number, number] = [245, 158, 11];
    const setFill = (c: [number, number, number]) => doc.setFillColor(c[0], c[1], c[2]);
    const setDraw = (c: [number, number, number]) => doc.setDrawColor(c[0], c[1], c[2]);
    const setText = (c: [number, number, number]) => doc.setTextColor(c[0], c[1], c[2]);

    const sectionBar = (text: string, bx: number, by: number, bw: number, h = 7) => {
      setFill(primary);
      doc.rect(bx, by, bw, h, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      setText([255, 255, 255]);
      doc.text(text.toUpperCase(), bx + 3, by + h / 2 + 1.4);
    };

    // ---------- Header ----------
    const logo = advancedTemplate?.leftLogo || advancedTemplate?.rightLogo;
    const logoSize = 16;
    if (logo) {
      try {
        const format = logo.includes('data:image/jpeg') ? 'JPEG' : 'PNG';
        doc.addImage(logo, format, margin, y, logoSize, logoSize);
      } catch (error) {
        console.error('Error adding logo to summary PDF', error);
      }
    } else {
      setFill(primary);
      doc.roundedRect(margin, y, logoSize, logoSize, 3, 3, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      setText([255, 255, 255]);
      doc.text(String(clientName || 'F').trim().charAt(0).toUpperCase() || 'F', margin + logoSize / 2, y + logoSize / 2 + 1.5, { align: 'center' });
    }

    // Fit long titles: shrink first, then truncate with an ellipsis as a last resort
    // so it never collides with the logo on either side.
    const fitText = (text: string, maxWidth: number, startSize: number, minSize: number, font: 'bold' | 'normal' = 'bold') => {
      let size = startSize;
      doc.setFont('helvetica', font);
      doc.setFontSize(size);
      while (size > minSize && doc.getTextWidth(text) > maxWidth) {
        size -= 0.5;
        doc.setFontSize(size);
      }
      let display = text;
      while (display.length > 1 && doc.getTextWidth(`${display}…`) > maxWidth) {
        display = display.slice(0, -1);
      }
      if (display !== text) display = `${display}…`;
      return { text: display, size };
    };

    const title = advancedTemplate.reportTitle || 'ENERGY METER SUMMARY REPORT';
    const titleMaxWidth = contentWidth - 2 * (logoSize + 6);
    const fittedTitle = fitText(title, titleMaxWidth, 19, 12, 'bold');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(fittedTitle.size);
    setText(ink);
    doc.setCharSpace(0.2);
    doc.text(fittedTitle.text, pageWidth / 2, y + 8, { align: 'center' });
    doc.setCharSpace(0);

    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10.5);
    setText(slate);
    doc.text('Trend & Summary Overview', pageWidth / 2, y + 15, { align: 'center' });

    y += logoSize + 6;

    const clientLine = `Client: ${clientName}    |    Generated By: ${downloadedBy}`;
    const fittedClientLine = fitText(clientLine, contentWidth - 10, 9.5, 7.5, 'normal');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(fittedClientLine.size);
    setText(slate);
    doc.text(fittedClientLine.text, pageWidth / 2, y, { align: 'center' });
    y += 7;

    setDraw(primary);
    doc.setLineWidth(0.8);
    doc.line(margin, y, pageWidth - margin, y);
    y += 6;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    setText(slate);
    doc.text(
      `Date Range: ${fmtDateTime(startDate)}  -  ${fmtDateTime(endDate)}      |      Report Generated On: ${new Date().toLocaleString()}`,
      pageWidth / 2,
      y,
      { align: 'center' }
    );
    y += 8;

    // ---------- Overview + Meter Summary ----------
    const overviewW = contentWidth * 0.64;
    const meterSummaryW = contentWidth - overviewW - 6;
    const headerBarH = 7;
    const boxH = 37;

    setDraw(border);
    doc.setLineWidth(0.3);
    doc.roundedRect(margin, y, overviewW, boxH, 2, 2, 'S');
    sectionBar('Overview (All Meters)', margin, y, overviewW, headerBarH);

    const tileColors: [number, number, number][] = [blue, green, amber];
    const tiles = [
      { label: 'AVERAGE VOLTAGE', value: `${summaryStats.avgVoltage.toFixed(1)} V`, sub: `Min ${summaryStats.minVoltage.toFixed(1)} V  |  Max ${summaryStats.maxVoltage.toFixed(1)} V` },
      { label: 'AVERAGE ACTIVE KW', value: `${summaryStats.avgActiveKw.toFixed(1)} kW`, sub: `Min ${summaryStats.minActiveKw.toFixed(1)} kW  |  Max ${summaryStats.maxActiveKw.toFixed(1)} kW` },
      { label: 'TOTAL CONSUMPTION', value: `${summaryStats.totalConsumption.toFixed(1)} kWh`, sub: `Peak Active Power: ${summaryStats.peakKw.toFixed(1)} kW` },
    ];
    const tileW = (overviewW - 8) / 3;
    tiles.forEach((tile, i) => {
      const tx = margin + 4 + i * tileW;
      const ty = y + headerBarH;

      setFill(tileColors[i]);
      doc.circle(tx + 1, ty + 6, 1.1, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.6);
      setText(muted);
      doc.text(tile.label, tx + 3.5, ty + 7);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      setText(ink);
      doc.text(tile.value, tx, ty + 16.5);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.6);
      setText(muted);
      doc.text(tile.sub, tx, ty + 22.5, { maxWidth: tileW - 5 });

      if (i < tiles.length - 1) {
        setDraw(faint);
        doc.setLineWidth(0.25);
        doc.line(tx + tileW - 4, ty + 2, tx + tileW - 4, y + boxH - 3);
      }
    });

    const msX = margin + overviewW + 6;
    setDraw(border);
    doc.roundedRect(msX, y, meterSummaryW, boxH, 2, 2, 'S');
    sectionBar('Meter Summary', msX, y, meterSummaryW, headerBarH);

    const meterCountRows: [string, string, [number, number, number]][] = [
      ['Total Meters', String(meterCounts.total), muted],
      ['Active Meters', String(meterCounts.active), green],
      ['Inactive Meters', String(meterCounts.inactive), border],
    ];
    meterCountRows.forEach((r, i) => {
      const ry = y + headerBarH + 8 + i * 8;
      setFill(r[2]);
      doc.circle(msX + 5, ry - 1.3, 1.3, 'F');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      setText(slate);
      doc.text(r[0], msX + 9, ry);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      setText(ink);
      doc.text(r[1], msX + meterSummaryW - 4, ry, { align: 'right' });
    });

    y += boxH + 9;

    // ---------- Trend chart ----------
    if (trendData.length > 0) {
      sectionBar('Energy Meter Trend (All Meters)', margin, y, contentWidth, headerBarH);
      y += headerBarH + 6;

      const legendItems: [string, [number, number, number]][] = [
        ['Voltage (V)', blue],
        ['Active kW', green],
        ['Energy (kWh)', amber],
      ];
      let legendX = pageWidth - margin - 75;
      legendItems.forEach(([label, color]) => {
        setFill(color);
        doc.rect(legendX, y - 3.2, 3, 3, 'F');
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        setText(slate);
        doc.text(label, legendX + 4.5, y - 0.5);
        legendX += 25;
      });
      y += 7;

      const chartX0 = margin + 16;
      const chartX1 = pageWidth - margin - 20;
      const chartY0 = y;
      const chartH = 58;
      const chartY1 = chartY0 + chartH;

      const voltVals = trendData.map((d) => d.avgVoltage).filter((v) => v > 0);
      const vMin = voltVals.length ? Math.max(0, Math.floor(Math.min(...voltVals) / 10) * 10 - 10) : 0;
      const vMax = voltVals.length ? Math.ceil(Math.max(...voltVals) / 10) * 10 + 10 : 100;
      const kwMax = Math.max(10, Math.ceil((Math.max(...trendData.map((d) => d.avgActiveKw), 0) * 1.2) / 10) * 10);
      const kwhMax = Math.max(10, Math.ceil((trendData[trendData.length - 1]?.cumulativeKwh || 0) * 1.1));

      setFill(panelBg);
      doc.rect(chartX0, chartY0, chartX1 - chartX0, chartH, 'F');

      setDraw(faint);
      doc.setLineWidth(0.2);
      const fracs = [0, 0.25, 0.5, 0.75, 1];
      fracs.forEach((frac) => {
        const gy = chartY1 - frac * chartH;
        doc.line(chartX0, gy, chartX1, gy);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.5);
        setText(blue);
        doc.text((vMin + frac * (vMax - vMin)).toFixed(0), chartX0 - 2, gy + 1, { align: 'right' });

        setText(green);
        doc.text((frac * kwMax).toFixed(0), chartX1 + 2, gy + 1);

        setText(amber);
        doc.text((frac * kwhMax).toFixed(0), chartX1 + 12, gy + 1);
      });

      setDraw(border);
      doc.setLineWidth(0.4);
      doc.rect(chartX0, chartY0, chartX1 - chartX0, chartH, 'S');

      const n = trendData.length;
      const xFor = (i: number) => (n <= 1 ? chartX0 : chartX0 + (i / (n - 1)) * (chartX1 - chartX0));
      // Hard clamp to the drawn box — a backstop so a single bad/outlier value (e.g.
      // an unscaled raw reading, or a today's-partial-bucket average sitting outside
      // the range the axis was sized for) can never send the line shooting off the
      // page; it just pins to the top/bottom edge instead, which reads as "off the
      // chart" rather than as a broken drawing.
      const clampY = (y: number) => Math.min(chartY1, Math.max(chartY0, y));
      const yForVoltage = (v: number) => clampY(chartY1 - ((v - vMin) / ((vMax - vMin) || 1)) * chartH);
      const yForKw = (v: number) => clampY(chartY1 - (v / (kwMax || 1)) * chartH);
      const yForKwh = (v: number) => clampY(chartY1 - (v / (kwhMax || 1)) * chartH);

      const showMarkers = n <= 24;
      const drawSeries = (getY: (v: number) => number, key: 'avgVoltage' | 'avgActiveKw' | 'cumulativeKwh', color: [number, number, number]) => {
        doc.setDrawColor(color[0], color[1], color[2]);
        doc.setLineWidth(0.7);
        doc.setLineCap?.('round');
        doc.setLineJoin?.('round');
        for (let i = 1; i < n; i += 1) {
          const prevVal = Number(trendData[i - 1][key]);
          const curVal = Number(trendData[i][key]);
          if (!Number.isFinite(prevVal) || !Number.isFinite(curVal)) continue; // skip NaN/undefined gaps instead of drawing to a garbage coordinate
          doc.line(xFor(i - 1), getY(prevVal), xFor(i), getY(curVal));
        }
        if (showMarkers) {
          setFill([255, 255, 255]);
          setDraw(color);
          doc.setLineWidth(0.5);
          for (let i = 0; i < n; i += 1) {
            const val = Number(trendData[i][key]);
            if (!Number.isFinite(val)) continue;
            doc.circle(xFor(i), getY(val), 0.9, 'FD');
          }
        }
      };
      drawSeries(yForVoltage, 'avgVoltage', blue);
      drawSeries(yForKw, 'avgActiveKw', green);
      drawSeries(yForKwh, 'cumulativeKwh', amber);

      const maxLabels = 8;
      const step = Math.max(1, Math.ceil(n / maxLabels));
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      setText(muted);
      for (let i = 0; i < n; i += step) {
        doc.text(trendData[i].label, xFor(i), chartY1 + 4, { align: 'center', maxWidth: 18 });
      }
      if ((n - 1) % step !== 0) {
        doc.text(trendData[n - 1].label, xFor(n - 1), chartY1 + 4, { align: 'center', maxWidth: 18 });
      }

      y = chartY1 + 9;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(7.5);
      setText(muted);
      doc.text('Note: Energy (kWh) is cumulative over the selected period.', margin, y);
      y += 9;
    }

    // ---------- Meter-wise summary table ----------
    sectionBar('Meter-Wise Summary', margin, y, contentWidth, headerBarH);
    y += headerBarH + 3;

    (doc as any).autoTable({
      startY: y,
      head: [
        [
          { content: 'Meter Name', rowSpan: 2, styles: { valign: 'middle' } },
          { content: 'Average Voltage (V)', colSpan: 3, styles: { halign: 'center' } },
          { content: 'Average Active kW', colSpan: 3, styles: { halign: 'center' } },
          { content: 'Total Consumption (kWh)', rowSpan: 2, styles: { valign: 'middle' } },
          { content: '% of Total', rowSpan: 2, styles: { valign: 'middle' } },
        ],
        ['Average', 'Min', 'Max', 'Average', 'Min', 'Max'],
      ],
      body: meterRows.map((row) => [
        row.meterName,
        row.avgVoltage.toFixed(2),
        row.minVoltage.toFixed(2),
        row.maxVoltage.toFixed(2),
        row.avgActiveKw.toFixed(2),
        row.minActiveKw.toFixed(2),
        row.maxActiveKw.toFixed(2),
        row.totalConsumption.toFixed(2),
        `${row.percentOfTotal.toFixed(2)} %`,
      ]),
      foot: [[
        'TOTAL / AVERAGE',
        summaryStats.avgVoltage.toFixed(2),
        summaryStats.minVoltage.toFixed(2),
        summaryStats.maxVoltage.toFixed(2),
        summaryStats.avgActiveKw.toFixed(2),
        summaryStats.minActiveKw.toFixed(2),
        summaryStats.maxActiveKw.toFixed(2),
        summaryStats.totalConsumption.toFixed(2),
        '100.00 %',
      ]],
      theme: 'grid',
      headStyles: { fillColor: primary, textColor: [255, 255, 255], fontSize: 8, halign: 'center', lineColor: border, lineWidth: 0.2 },
      footStyles: { fillColor: faint, textColor: ink, fontStyle: 'bold', fontSize: 8, lineColor: border, lineWidth: 0.2 },
      bodyStyles: { fontSize: 7.5, halign: 'center', lineColor: border, lineWidth: 0.15 },
      alternateRowStyles: { fillColor: panelBg },
      columnStyles: { 0: { halign: 'left' } },
      margin: { left: margin, right: margin, top: 15 },
    });
    y = (doc as any).lastAutoTable.finalY + 8;

    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7.5);
    setText(muted);
    doc.text('Note: All values are based on energy meter readings recorded during the selected period.', margin, y);

    // ---------- Frame, footer & signature (every page) ----------
    const footerText = advancedTemplate.footerText || 'Generated by FACTREON - Factory Management System';
    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);

      setDraw(border);
      doc.setLineWidth(0.5);
      doc.roundedRect(7, 7, pageWidth - 14, pageHeight - 14, 3, 3, 'S');

      setDraw(primary);
      doc.setLineWidth(0.6);
      doc.line(margin, pageHeight - 16, pageWidth - margin, pageHeight - 16);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      setText(muted);
      doc.text(footerText, margin, pageHeight - 11);
      doc.setFont('helvetica', 'bold');
      doc.text(`Page ${page} of ${pageCount}`, pageWidth - margin, pageHeight - 11, { align: 'right' });

      if (page === pageCount) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        setText(slate);
        doc.text('Checked by : ____________________', margin, pageHeight - 21);
        doc.text('Date checked : ____________________', pageWidth - margin - 62, pageHeight - 21);
      }
    }

    const fileName = `energy_meter_summary_${Date.now()}.pdf`;
    doc.save(fileName);
    return meterRows.length;
  };

  const downloadSummaryPdf = async (startDate: string, endDate: string) => {
    if (!summaryStats || summaryTableRows.length === 0) {
      toast.warning('Please fetch the summary report before exporting');
      return 0;
    }

    return await createSummaryPdf(startDate, endDate, summaryTableRows, summaryTrendData, summaryMeterCounts);
  };

  // When preset changes, auto-fill the date fields
  const applyPreset = (preset: string) => {
    setDateRange(preset);
    const now = new Date();
    const fmt = (d: Date) => toLocalDateStr(d);
    
    const currentHrs = String(now.getHours()).padStart(2, '0');
    const currentMins = String(now.getMinutes()).padStart(2, '0');
    const currentTimeStr = `${currentHrs}:${currentMins}`;

    switch (preset) {
      case 'today':
        setCustomStart(fmt(now)); setCustomEnd(fmt(now));
        setCustomStartTime('00:00'); setCustomEndTime(currentTimeStr);
        break;
      case '1d': {
        const s = new Date(now); s.setDate(s.getDate() - 1);
        setCustomStart(fmt(s)); setCustomEnd(fmt(now));
        setCustomStartTime(currentTimeStr); setCustomEndTime(currentTimeStr);
        break;
      }
      case '7d': {
        const s = new Date(now); s.setDate(s.getDate() - 7);
        setCustomStart(fmt(s)); setCustomEnd(fmt(now));
        setCustomStartTime('00:00'); setCustomEndTime(currentTimeStr);
        break;
      }
      case '30d': {
        const s = new Date(now); s.setDate(s.getDate() - 30);
        setCustomStart(fmt(s)); setCustomEnd(fmt(now));
        setCustomStartTime('00:00'); setCustomEndTime(currentTimeStr);
        break;
      }
      case '6m': {
        const s = new Date(now); s.setMonth(s.getMonth() - 6);
        setCustomStart(fmt(s)); setCustomEnd(fmt(now));
        setCustomStartTime('00:00'); setCustomEndTime(currentTimeStr);
        break;
      }
      case '1y': {
        const s = new Date(now); s.setFullYear(s.getFullYear() - 1);
        setCustomStart(fmt(s)); setCustomEnd(fmt(now));
        setCustomStartTime('00:00'); setCustomEndTime(currentTimeStr);
        break;
      }
      // 'custom' — leave fields as-is for manual editing
    }
  };

  const resolvePlcTagSelection = () => {
    // `tags` is already float-only (see the fetch above), so intersecting against
    // it here also guards a saved report template created before that filter
    // existed — its config.tagIds could still be pointing at a boolean tag id.
    const floatTagIdSet = new Set(tags.map((tag: any) => String(tag.id)));

    const template = plcTemplates.find((t) => t.id === selectedTemplate);
    const templateTagIds = Array.isArray(template?.config?.tagIds)
      ? template.config.tagIds.map((id: any) => String(id)).filter((id: string) => floatTagIdSet.has(id))
      : [];
    const hasManualFilter = selectedSection !== 'all' || selectedTagIds.length > 0;
    const useTemplateTags = templateTagIds.length > 0 && !hasManualFilter;
    const finalTagIds = useTemplateTags ? templateTagIds : effectiveTagIds;

    return {
      hasTemplateTags: useTemplateTags,
      finalTagIds,
    };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const { startDate, endDate } = validateDateRange();

      let fetchedData: any[] = [];
      const query = new URLSearchParams({
        limit: '10000',
        startDate,
        endDate,
      });

      if (deviceType === 'plc') {
        const { hasTemplateTags, finalTagIds } = resolvePlcTagSelection();

        if (!hasTemplateTags && !selectedPlc) {
          toast.warning('Please select a PLC or report template');
          return;
        }

        const resp = await adminApi.getHistoricalData(token, clientId, {
          tagIds: finalTagIds.length > 0 ? finalTagIds.join(',') : undefined,
          plcId: hasTemplateTags ? undefined : selectedPlc,
          startDate,
          endDate,
          sectionId: selectedSection === 'all' ? undefined : selectedSection,
          interval: intervalParam === '0' ? undefined : intervalParam,
          limit: 2000 
        });
        const rawData = resp.data || [];
        
        // Group by FormattedTime for multi-line Recharts charting
        const grouped: any = {};
        const tagsSeen = new Set<string>();
        rawData.forEach((d: any) => {
          const t = new Date(d.timestamp).toLocaleString();
          if (!grouped[t]) {
            grouped[t] = { formattedTime: t, ...d }; // keep base fields
          }
          grouped[t][d.tag_name] = Number(d.value);
          tagsSeen.add(d.tag_name);
        });

        fetchedData = Object.values(grouped);
        setActiveTagNames(Array.from(tagsSeen));

      } else if (deviceType === 'scanner') {
        if (!selectedScanner) {
          toast.warning('Please select a scanner');
          return;
        }
        const query = new URLSearchParams({
          limit: '10000',
          startDate,
          endDate,
        });

        const resp = await fetch(`${API_BASE}/api/clients/${clientId}/scanners/readings?${query.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const resData = await resp.json();
        if (!resp.ok) {
          throw new Error(resData?.error || 'Failed to fetch scanner readings');
        }

        const scannerRows = (resData.readings || []).filter((r: any) => String(r.scanner_id) === String(selectedScanner));
        const grouped: any = {};
        const tagsSeen = new Set<string>();
        scannerRows.forEach((reading: any) => {
          const timestamp = reading.reading_timestamp || reading.timestamp;
          const formattedTime = new Date(timestamp).toLocaleString();
          const tagName = String(reading.tag_name || reading.tagName || reading.tag_id || 'Value');
          if (!grouped[formattedTime]) {
            grouped[formattedTime] = { formattedTime, timestamp };
          }
          grouped[formattedTime][tagName] = Number(reading.value);
          tagsSeen.add(tagName);
        });

        fetchedData = Object.values(grouped);
        setActiveTagNames(Array.from(tagsSeen));
      } else if (deviceType === 'energy_meter') {
        if (!selectedMeter) {
          toast.warning('Please select an Energy Meter');
          return;
        }
        const query = new URLSearchParams({
          limit: '10000',
          startDate,
          endDate,
        });

        const resp = await fetch(`${API_BASE}/api/clients/${clientId}/energy-meters/${selectedMeter}/readings?${query.toString()}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!resp.ok) {
          const payload = await resp.json();
          throw new Error(payload?.error || 'Failed to fetch energy meter readings');
        }
        const resData = await resp.json();

        fetchedData = (resData.readings || [])
          .filter((d: any) => {
            const ts = new Date(d.timestamp).getTime();
            return ts >= new Date(startDate).getTime() && ts <= new Date(endDate).getTime();
          })
          .map((d: any) => ({
            ...d,
            formattedTime: new Date(d.timestamp).toLocaleString(),
            Measured_Value: Number(d.value)
          }));
        setActiveTagNames(['Measured_Value']);
      } else if (deviceType === 'summary') {
        if (meters.length === 0) {
          toast.warning('No energy meters loaded for summary report');
          return;
        }

        const selectedMeterIds = exportAllMeters
          ? meters.map((m) => String(m.id)).filter(Boolean)
          : selectedExportMeterIds.length > 0
          ? selectedExportMeterIds
          : selectedMeter
            ? [selectedMeter]
            : [];

        if (selectedMeterIds.length === 0) {
          toast.warning('Please select at least one Energy Meter for summary');
          return;
        }

        const query = new URLSearchParams({
          limit: '10000',
          startDate,
          endDate,
        });
        if (intervalParam && intervalParam !== '0') {
          query.set('interval', intervalParam);
        }

        const meterNameMap = new Map(meters.map((meter: any) => [String(meter.id), String(meter.name || meter.id || 'Meter')]));

        const meterSummaries = await Promise.all(selectedMeterIds.map(async (meterId) => {
          const resp = await fetch(`${API_BASE}/api/clients/${clientId}/energy-meters/${meterId}/readings?${query.toString()}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!resp.ok) {
            const payload = await resp.json();
            throw new Error(payload?.error || 'Failed to fetch energy meter readings');
          }
          const data = await resp.json();
          return {
            meterId,
            meterName: meterNameMap.get(meterId) || meterId,
            readings: (data.readings || [])
              .filter((row: any) => {
                const ts = new Date(row.timestamp).getTime();
                return ts >= new Date(startDate).getTime() && ts <= new Date(endDate).getTime();
              })
              .map((row: any) => ({
                meterId,
                timestamp: row.timestamp,
                value: Number(row.value),
                tagName: String(row.tag_name || row.tagName || ''),
                unit: String(row.unit || '').toLowerCase(),
              }))
          };
        }));

        const allReadings = meterSummaries.flatMap((m) => m.readings);
        const activeReadings = allReadings.filter((r) => isActiveKwTag(r.tagName, r.unit));
        const voltageReadings = allReadings.filter((r) => isVoltageTag(r.tagName, r.unit));

        const avgActiveKw = activeReadings.length > 0
          ? activeReadings.reduce((sum, r) => sum + r.value, 0) / activeReadings.length
          : 0;
        const avgVoltage = voltageReadings.length > 0
          ? voltageReadings.reduce((sum, r) => sum + r.value, 0) / voltageReadings.length
          : 0;
        const peakKw = activeReadings.length > 0
          ? Math.max(...activeReadings.map((r) => r.value))
          : 0;

        const totalConsumption = selectedMeterIds.reduce((sum, meterId) => {
          const meterReadings = allReadings.filter((r) => r.meterId === meterId && isKwhTag(r.unit));
          const minByTag = new Map<string, number>();
          const maxByTag = new Map<string, number>();
          meterReadings.forEach((r) => {
            const key = `${r.meterId}_${r.tagName}`;
            minByTag.set(key, Math.min(minByTag.get(key) ?? r.value, r.value));
            maxByTag.set(key, Math.max(maxByTag.get(key) ?? r.value, r.value));
          });
          const meterConsumption = Array.from(maxByTag.keys()).reduce((meterSum, key) => {
            return meterSum + Math.max(0, (maxByTag.get(key) ?? 0) - (minByTag.get(key) ?? 0));
          }, 0);
          return sum + meterConsumption;
        }, 0);

        const activeVoltageReadings = voltageReadings.map((r) => r.value);
        const activeKwValues = activeReadings.map((r) => r.value);
        const minVoltage = activeVoltageReadings.length > 0 ? Math.min(...activeVoltageReadings) : 0;
        const maxVoltage = activeVoltageReadings.length > 0 ? Math.max(...activeVoltageReadings) : 0;
        const minActiveKw = activeKwValues.length > 0 ? Math.min(...activeKwValues) : 0;
        const maxActiveKw = activeKwValues.length > 0 ? Math.max(...activeKwValues) : 0;

        const meterSummaryMap = new Map<string, any>();
        const totalConsumptionByMeter = new Map<string, number>();

        meterSummaries.forEach((meter) => {
          const voltageVals: number[] = [];
          const activeKwVals: number[] = [];
          const kwhMinByTag = new Map<string, number>();
          const kwhMaxByTag = new Map<string, number>();

          meter.readings.forEach((reading: any) => {
            if (isVoltageTag(reading.tagName, reading.unit)) {
              voltageVals.push(reading.value);
            }
            if (isActiveKwTag(reading.tagName, reading.unit)) {
              activeKwVals.push(reading.value);
            }
            if (isKwhTag(reading.unit)) {
              // kWh readings are cumulative meter-register values, so consumption for the
              // period is (max - min), not a raw sum of every reading (matches totalConsumption above).
              const key = reading.tagName;
              kwhMinByTag.set(key, Math.min(kwhMinByTag.get(key) ?? reading.value, reading.value));
              kwhMaxByTag.set(key, Math.max(kwhMaxByTag.get(key) ?? reading.value, reading.value));
            }
          });

          const meterTotalKwh = Array.from(kwhMaxByTag.keys()).reduce((sum, key) => {
            return sum + Math.max(0, (kwhMaxByTag.get(key) ?? 0) - (kwhMinByTag.get(key) ?? 0));
          }, 0);

          const meterId = String(meter.meterId);
          meterSummaryMap.set(meterId, {
            meterId,
            meterName: String(meter.meterName || meter.meterId || 'Meter'),
            avgVoltage: voltageVals.length > 0 ? Number((voltageVals.reduce((s, v) => s + v, 0) / voltageVals.length).toFixed(2)) : 0,
            minVoltage: voltageVals.length > 0 ? Number(Math.min(...voltageVals).toFixed(2)) : 0,
            maxVoltage: voltageVals.length > 0 ? Number(Math.max(...voltageVals).toFixed(2)) : 0,
            avgActiveKw: activeKwVals.length > 0 ? Number((activeKwVals.reduce((s, v) => s + v, 0) / activeKwVals.length).toFixed(2)) : 0,
            minActiveKw: activeKwVals.length > 0 ? Number(Math.min(...activeKwVals).toFixed(2)) : 0,
            maxActiveKw: activeKwVals.length > 0 ? Number(Math.max(...activeKwVals).toFixed(2)) : 0,
            totalConsumption: Number(meterTotalKwh.toFixed(2)),
          });
          totalConsumptionByMeter.set(meterId, meterTotalKwh);
        });

        const totalPeriodConsumption = Array.from(totalConsumptionByMeter.values()).reduce((sum, v) => sum + v, 0);
        const tableRows = Array.from(meterSummaryMap.values()).map((row) => ({
          ...row,
          percentOfTotal: totalPeriodConsumption > 0 ? Number(((row.totalConsumption / totalPeriodConsumption) * 100).toFixed(2)) : 0,
        }));

        // Trend series: hourly buckets for a single-day range, daily buckets otherwise
        const rangeStartMs = new Date(startDate).getTime();
        const rangeEndMs = new Date(endDate).getTime();
        const spanMs = Math.max(rangeEndMs - rangeStartMs, 1);
        const isSingleDayRange = spanMs <= 26 * 60 * 60 * 1000;
        const bucketMs = isSingleDayRange ? 60 * 60 * 1000 : 24 * 60 * 60 * 1000;
        const bucketAnchor = isSingleDayRange
          ? Math.floor(rangeStartMs / bucketMs) * bucketMs
          : new Date(new Date(rangeStartMs).setHours(0, 0, 0, 0)).getTime();

        const bucketLabel = (ms: number) => {
          const d = new Date(ms);
          return isSingleDayRange
            ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : d.toLocaleDateString([], { day: '2-digit', month: 'short' });
        };

        const trendBuckets: { start: number; end: number; label: string; voltage: number[]; kw: number[]; kwhByKey: Map<string, { min: number; max: number }> }[] = [];
        for (let b = bucketAnchor; b < rangeEndMs; b += bucketMs) {
          trendBuckets.push({ start: b, end: b + bucketMs, label: bucketLabel(b), voltage: [], kw: [], kwhByKey: new Map() });
        }

        allReadings.forEach((r) => {
          const ts = new Date(r.timestamp).getTime();
          const bucket = trendBuckets.find((bkt) => ts >= bkt.start && ts < bkt.end);
          if (!bucket) return;
          if (isVoltageTag(r.tagName, r.unit)) bucket.voltage.push(r.value);
          if (isActiveKwTag(r.tagName, r.unit)) bucket.kw.push(r.value);
          if (isKwhTag(r.unit)) {
            // kWh readings are cumulative meter-register values, so a bucket's consumption
            // is the (max - min) seen within that window, not a raw sum of the readings.
            const key = `${r.meterId}_${r.tagName}`;
            const existing = bucket.kwhByKey.get(key);
            if (existing) {
              existing.min = Math.min(existing.min, r.value);
              existing.max = Math.max(existing.max, r.value);
            } else {
              bucket.kwhByKey.set(key, { min: r.value, max: r.value });
            }
          }
        });

        let runningKwh = 0;
        const trendData = trendBuckets.map((bkt) => {
          const bucketKwh = Array.from(bkt.kwhByKey.values()).reduce((sum, { min, max }) => sum + Math.max(0, max - min), 0);
          runningKwh += bucketKwh;
          return {
            label: bkt.label,
            avgVoltage: bkt.voltage.length > 0 ? Number((bkt.voltage.reduce((s, v) => s + v, 0) / bkt.voltage.length).toFixed(2)) : 0,
            avgActiveKw: bkt.kw.length > 0 ? Number((bkt.kw.reduce((s, v) => s + v, 0) / bkt.kw.length).toFixed(2)) : 0,
            kwh: Number(bucketKwh.toFixed(2)),
            cumulativeKwh: Number(runningKwh.toFixed(2)),
          };
        });

        const activeMeterCount = meterSummaries.filter((m) => m.readings.length > 0).length;
        const meterCounts = {
          total: selectedMeterIds.length,
          active: activeMeterCount,
          inactive: selectedMeterIds.length - activeMeterCount,
        };

        setSummaryStats({
          avgVoltage: Number(avgVoltage.toFixed(2)),
          minVoltage: Number(minVoltage.toFixed(2)),
          maxVoltage: Number(maxVoltage.toFixed(2)),
          avgActiveKw: Number(avgActiveKw.toFixed(2)),
          minActiveKw: Number(minActiveKw.toFixed(2)),
          maxActiveKw: Number(maxActiveKw.toFixed(2)),
          totalConsumption: Number(totalConsumption.toFixed(2)),
          peakKw: Number(peakKw.toFixed(2)),
          meterCount: selectedMeterIds.length,
        });
        setSummaryTableRows(tableRows);
        setSummaryTrendData(trendData);
        setSummaryMeterCounts(meterCounts);

        const buckets: Record<string, { formattedTime: string; activeValues: number[]; voltageValues: number[]; kwhValues: number[]; peakValues: number[] }> = {};
        allReadings.forEach((reading) => {
          const formattedTime = new Date(reading.timestamp).toLocaleString();
          if (!buckets[formattedTime]) {
            buckets[formattedTime] = { formattedTime, activeValues: [], voltageValues: [], kwhValues: [], peakValues: [] };
          }
          if (isActiveKwTag(reading.tagName, reading.unit)) {
            buckets[formattedTime].activeValues.push(reading.value);
            buckets[formattedTime].peakValues.push(reading.value);
          }
          if (isVoltageTag(reading.tagName, reading.unit)) {
            buckets[formattedTime].voltageValues.push(reading.value);
          }
          if (isKwhTag(reading.unit)) {
            buckets[formattedTime].kwhValues.push(reading.value);
          }
        });

        fetchedData = Object.values(buckets)
          .sort((a, b) => new Date(a.formattedTime).getTime() - new Date(b.formattedTime).getTime())
          .map((bucket) => ({
            formattedTime: bucket.formattedTime,
            avgVoltage: bucket.voltageValues.length > 0
              ? Number((bucket.voltageValues.reduce((sum, v) => sum + v, 0) / bucket.voltageValues.length).toFixed(2))
              : null,
            avgActiveKw: bucket.activeValues.length > 0
              ? Number((bucket.activeValues.reduce((sum, v) => sum + v, 0) / bucket.activeValues.length).toFixed(2))
              : null,
            totalConsumption: bucket.kwhValues.length > 0
              ? Number(bucket.kwhValues.reduce((sum, v) => sum + v, 0).toFixed(2))
              : null,
            peakKw: bucket.peakValues.length > 0
              ? Number(Math.max(...bucket.peakValues).toFixed(2))
              : null,
          }));
        setActiveTagNames(['avgVoltage', 'avgActiveKw', 'totalConsumption', 'peakKw']);
      }

      setData(fetchedData.reverse()); // Charting flows left-to-right
      toast.success(`Loaded ${fetchedData.length} records`);

    } catch (err: any) {
      toast.error(err.message || 'Failed to fetch historical data');
    } finally {
      setLoading(false);
    }
  };

  const downloadPlcPdf = async (startDate: string, endDate: string, hasTemplateTags: boolean, finalTagIds: string[]) => {
    const resp = await adminApi.getHistoricalData(token, clientId, {
      tagIds: finalTagIds.length > 0 ? finalTagIds.join(',') : undefined,
      plcId: hasTemplateTags ? undefined : selectedPlc,
      startDate,
      endDate,
      sectionId: selectedSection === 'all' ? undefined : selectedSection,
      interval: intervalParam === '0' ? undefined : intervalParam,
      limit: 10000,
    });
    const rawData: any[] = resp.data || [];

    if (rawData.length === 0) {
      throw new Error('No data found for the selected filters');
    }

    // Pivot rows: { timestamp, tag1: val1, tag2: val2, ... }
    const rowMap = new Map<string, any>();
    const tagMeta = new Map<string, { unit: string; plcName: string }>();

    rawData.forEach((d: any) => {
      const tsKey = new Date(d.timestamp).toISOString().slice(0, 19) + 'Z';
      if (!rowMap.has(tsKey)) rowMap.set(tsKey, { timestamp: tsKey });
      const key = String(d.tag_name || d.tagName || '').trim().replace(/[^a-z0-9_]/gi, '_');
      if (key) {
        rowMap.get(tsKey)[key] = d.value;
        if (!tagMeta.has(key)) tagMeta.set(key, { unit: String(d.unit || ''), plcName: String(d.plc_name || d.plcName || '') });
      }
    });

    const reportRows = Array.from(rowMap.values())
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    // Build one section per PLC, or one combined section
    const plcGroups = new Map<string, { label: string; key: string; unit: string }[]>();
    tagMeta.forEach(({ unit, plcName }, key) => {
      const group = plcName || 'PLC Data';
      if (!plcGroups.has(group)) plcGroups.set(group, []);
      const label = key.replace(/_+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      plcGroups.get(group)!.push({ label, key, unit });
    });

    const dynamicSections = Array.from(plcGroups.entries()).map(([plcName, cols]) => ({
      id: `plc_${plcName.replace(/\s+/g, '_')}`,
      title: plcName,
      columns: cols.map(({ label, key, unit }) => ({
        id: key,
        label,
        dataKey: key,
        unit,
        format: 'number' as const,
      })),
    }));

    let fetchedTemplate: any = {};
    try { fetchedTemplate = await adminApi.getPDFTemplate(token, clientId); } catch { /* ignore */ }
    const adv = fetchedTemplate?.advancedTemplate || {};

    const template = {
      reportTitle: adv?.reportTitle || 'HISTORICAL PLC DATA REPORT',
      reportSubtitle: adv?.reportSubtitle || '',
      leftLogo: adv?.leftLogo,
      rightLogo: adv?.rightLogo,
      companyFields: [
        { label: 'COMPANY NAME', value: '{companyName}' },
        { label: 'USER NAME', value: '{downloadedBy}' },
      ],
      showDateRange: adv?.showDateRange !== false,
      dateRangeLabel: 'Report Period',
      sections: dynamicSections,
      showCheckedBy: adv?.showCheckedBy !== false,
      showVerifiedBy: adv?.showVerifiedBy !== false,
      showGeneratedBy: adv?.showGeneratedBy !== false,
      footerText: adv?.footerText || '',
      includeCharts: adv?.includeCharts ?? false,
      primaryColor: adv?.primaryColor || '#000000',
      borderStyle: adv?.borderStyle || 'single',
      fontSize: adv?.fontSize || 'small',
    };

    const authSession = authSessionStorage.get();
    const clientName = authSession?.client?.name || authSession?.clientName || 'Client';
    const downloadedBy = authSession?.user?.name || authSession?.name || authSession?.user?.email || 'User';

    // Resolve PLC name and section name for display in PDF header
    const selectedPlcObj = plcs.find((p: any) => String(p.id) === String(selectedPlc));
    const selectedPlcName = selectedPlcObj?.name || selectedPlcObj?.plcName || (hasTemplateTags ? 'All PLCs (Template)' : '');
    const selectedSectionObj = sections.find((s: any) => String(s.id) === String(selectedSection));
    const selectedSectionName = (!selectedSection || selectedSection === 'all') ? 'All Sections' : (selectedSectionObj?.name || selectedSection);

    await generateCustomPLCReport([], reportRows, template, clientName, new Date(startDate), new Date(endDate), {
      companyName: clientName,
      downloadedBy,
      plcName: selectedPlcName || undefined,
      sectionName: selectedSectionName || undefined,
    });

    return reportRows.length;
  };

  const downloadScannerExcel = async (startDate: string, endDate: string, format: string) => {
    try {
      const resp = await fetch(`${API_BASE}/api/clients/${clientId}/scanners/readings?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&limit=10000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const payload = await resp.json();
      if (!resp.ok) throw new Error(payload.error || 'Failed to fetch scanner readings');
      
      const readings = payload.readings || [];
      const scanner = scanners.find((s) => s.id === selectedScanner);
      const scannerReadings = readings.filter((r: any) => r.scanner_id === selectedScanner);
      
      if (scannerReadings.length === 0) {
        toast.warning('No data found for this period');
        return 0;
      }

      // Group by timestamp
      const rowsByTime = new Map<string, any>();
      const tagMeta = new Map<string, { label: string; unit: string; key: string }>();
      scannerReadings.forEach((r: any) => {
        const timestamp = r.reading_timestamp || r.timestamp;
        const tsKey = new Date(timestamp).toISOString().slice(0, 19) + 'Z';
        const ts = new Date(timestamp).toLocaleString('en-IN');
        if (!rowsByTime.has(ts)) {
          rowsByTime.set(ts, { Timestamp: ts, timestamp: tsKey });
        }
        const row = rowsByTime.get(ts);
        const label = String(r.tag_name || r.tagName || r.tag_id || 'Value').trim();
        const key = label.replace(/[^a-z0-9_]/gi, '_') || 'Value';
        // r.value can be a boolean (M-bit/coil reading) or missing — Number(undefined)
        // produces NaN, which .toFixed(2) would then stringify as the literal "NaN".
        const cellValue = typeof r.value === 'boolean'
          ? (r.value ? '1' : '0')
          : Number.isFinite(Number(r.value)) ? Number(r.value).toFixed(2) : '-';
        row[format === 'pdf' ? key : `${label} ${r.unit ? `(${r.unit})` : ''}`] = cellValue;
        if (!tagMeta.has(key)) {
          tagMeta.set(key, { label, unit: String(r.unit || ''), key });
        }
      });

      // Enforce the selected Report Interval. Without this, every raw saved reading
      // becomes its own row — usually already close to the interval apart since
      // that's roughly how often the bridge saves data, but an extra reading can
      // slip in (e.g. a service restart), breaking the "exactly N minutes apart"
      // guarantee. Keep a row only once at least one full interval has passed
      // since the last kept row's timestamp.
      const intervalMs = intervalValue > 0
        ? intervalValue * (intervalUnit === 'seconds' ? 1000 : intervalUnit === 'hours' ? 3600000 : 60000)
        : 0;
      const sortedRows = Array.from(rowsByTime.values())
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      let filteredRows = sortedRows;
      if (intervalMs > 0) {
        filteredRows = [];
        let lastKeptMs: number | null = null;
        sortedRows.forEach((row) => {
          const ms = new Date(row.timestamp).getTime();
          if (lastKeptMs === null || ms - lastKeptMs >= intervalMs) {
            filteredRows.push(row);
            lastKeptMs = ms;
          }
        });
      }
      // Restore newest-first order to match the report's original display convention.
      const exportRows = [...filteredRows].reverse();
      if (format === 'pdf') {
        let fetchedTemplate: any = {};
        try { fetchedTemplate = await adminApi.getPDFTemplate(token, clientId); } catch { /* ignore */ }
        const adv = fetchedTemplate?.advancedTemplate || {};
        const dynamicSections = [{
          id: 'scanner_data',
          title: scanner?.name || 'Scanner Data',
          columns: Array.from(tagMeta.values()).map(({ label, key, unit }) => ({
            id: key,
            label,
            dataKey: key,
            unit,
            format: 'number' as const,
          })),
        }];
        const template = {
          reportTitle: adv?.reportTitle || 'SCANNER DATA LOG REPORT',
          reportSubtitle: adv?.reportSubtitle || '',
          leftLogo: adv?.leftLogo,
          rightLogo: adv?.rightLogo,
          companyFields: [
            { label: 'COMPANY NAME', value: '{companyName}' },
            { label: 'USER NAME', value: '{downloadedBy}' },
          ],
          showDateRange: adv?.showDateRange !== false,
          dateRangeLabel: 'Report Period',
          sections: dynamicSections,
          showCheckedBy: adv?.showCheckedBy !== false,
          showVerifiedBy: adv?.showVerifiedBy !== false,
          showGeneratedBy: adv?.showGeneratedBy !== false,
          footerText: adv?.footerText || '',
          includeCharts: adv?.includeCharts ?? false,
          primaryColor: adv?.primaryColor || '#000000',
          borderStyle: adv?.borderStyle || 'single',
          fontSize: adv?.fontSize || 'small',
        };
        const authSession = authSessionStorage.get();
        const clientName = authSession?.client?.name || authSession?.clientName || 'Client';
        const downloadedBy = authSession?.user?.name || authSession?.name || authSession?.user?.email || 'User';
        await generateCustomPLCReport([], exportRows, template, clientName, new Date(startDate), new Date(endDate), {
          companyName: clientName,
          downloadedBy,
          plcName: scanner?.name || 'Scanner',
          sectionName: 'Scanner Data',
        });
        return scannerReadings.length;
      }

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportRows);
      XLSX.utils.book_append_sheet(wb, ws, 'Scanner Data');
      XLSX.writeFile(wb, `Scanner_Report_${scanner?.name || 'Data'}_${new Date().getTime()}.xlsx`);

      return scannerReadings.length;
    } catch (err: any) {
      toast.error(err.message || 'Failed to generate scanner report');
      return 0;
    }
  };

  const downloadFile = async (format: 'excel' | 'pdf') => {
    try {
      const { startDate, endDate } = validateDateRange();
      const { hasTemplateTags, finalTagIds } = resolvePlcTagSelection();

      if (deviceType === 'plc' && !hasTemplateTags && !selectedPlc) {
        toast.warning('Please select a PLC or report template for export');
        return;
      }

      if (deviceType === 'scanner' && !selectedScanner) {
        toast.warning('Please select a scanner for export');
        return;
      }

      toast.info(`Generating ${format.toUpperCase()} report...`);

        if (deviceType === 'scanner') {
        const rowCount = await downloadScannerExcel(startDate, endDate, format);
        toast.success(`Report downloaded successfully (${rowCount} records)`);
        return;
      }

      if (deviceType === 'summary') {
        const rowCount = format === 'excel'
          ? await downloadSummaryExcel(startDate, endDate)
          : await downloadSummaryPdf(startDate, endDate);
        toast.success(`Summary report downloaded successfully (${rowCount} records)`);
        return;
      }

      if (deviceType === 'energy_meter') {
        const rowCount = format === 'excel'
          ? await downloadEnergyMeterExcel(startDate, endDate)
          : await downloadEnergyMeterPdf(startDate, endDate);
        toast.success(`Report downloaded successfully (${rowCount} records)`);
        return;
      }

      if (deviceType === 'plc' && format === 'pdf') {
        const rowCount = await downloadPlcPdf(startDate, endDate, hasTemplateTags, finalTagIds);
        toast.success(`Report downloaded successfully (${rowCount} records)`);
        return;
      }

      const blob = await adminApi.downloadHistoricalData(token, clientId, format, {
        tagIds: finalTagIds.length > 0 ? finalTagIds.join(',') : undefined,
        plcId: hasTemplateTags ? undefined : selectedPlc,
        startDate,
        endDate,
        sectionId: selectedSection === 'all' ? undefined : selectedSection,
        interval: intervalParam === '0' ? undefined : intervalParam,
        tzOffset: String(-new Date().getTimezoneOffset()),
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const extension = format === 'excel' ? 'xlsx' : format;
      link.download = `historical_report_${new Date().getTime()}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Report downloaded successfully');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to generate report');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            Reports
          </h2>
          <p className="text-muted-foreground mt-1">
            Select a source and date range, review the report, then export the same filtered data as Excel or PDF.
          </p>
        </div>
      </div>

      <Card className="bg-card border-border/60">
        <CardContent className="p-4 sm:p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            
            {/* Device Type Selector */}
            <div className="space-y-2">
              <Label className="text-xs uppercase font-bold text-muted-foreground">Source Type</Label>
              <div className="flex bg-muted/40 p-1 rounded-lg border border-border/40">
                {permissions?.livePlcData?.view !== false && (
                  <button
                    onClick={() => setDeviceType('plc')}
                    className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all flex items-center justify-center gap-2 ${deviceType === 'plc' ? 'bg-background shadow-sm border border-border/60' : 'text-muted-foreground hover:bg-muted/60'}`}
                  >
                    <Activity className="h-4 w-4" /> PLC
                  </button>
                )}
                {permissions?.energyConsumption?.view !== false && (
                  <button
                    onClick={() => setDeviceType('energy_meter')}
                    className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all flex items-center justify-center gap-2 ${deviceType === 'energy_meter' ? 'bg-background shadow-sm border border-border/60' : 'text-muted-foreground hover:bg-muted/60'}`}
                  >
                    <Database className="h-4 w-4" /> Meter
                  </button>
                )}
                {permissions?.scanners?.view !== false && (
                  <button
                    onClick={() => setDeviceType('scanner')}
                    className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all flex items-center justify-center gap-2 ${deviceType === 'scanner' ? 'bg-background shadow-sm border border-border/60' : 'text-muted-foreground hover:bg-muted/60'}`}
                  >
                    <Activity className="h-4 w-4" /> Scanner
                  </button>
                )}
                {permissions?.reports?.summaryReport === true && (
                  <button
                    onClick={() => setDeviceType('summary')}
                    className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all flex items-center justify-center gap-2 ${deviceType === 'summary' ? 'bg-background shadow-sm border border-border/60' : 'text-muted-foreground hover:bg-muted/60'}`}
                  >
                    <FileText className="h-4 w-4" /> Summarise Report
                  </button>
                )}
              </div>
            </div>

            {/* Target Selector */}
            {deviceType === 'plc' ? (
              <div className="space-y-3 col-span-1 lg:col-span-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase font-bold text-muted-foreground">PLC</Label>
                    <select
                      className="w-full h-9 rounded-md border border-input bg-background text-foreground [color-scheme:dark] px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      value={selectedPlc}
                      onChange={(e) => setSelectedPlc(e.target.value)}
                    >
                      <option value="">Select PLC...</option>
                      {plcs.map((plc) => (
                        <option key={plc.id} value={plc.id}>{plc.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase font-bold text-muted-foreground">Section</Label>
                    <select
                      className="w-full h-9 rounded-md border border-input bg-background text-foreground [color-scheme:dark] px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      value={selectedSection}
                      onChange={(e) => setSelectedSection(e.target.value)}
                    >
                      <option value="all">All Sections</option>
                      {sections.map((section: any) => (
                        <option key={section.id} value={section.id}>{section.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase font-bold text-muted-foreground">
                    Tags {selectedSection !== 'all' ? '(in selected section)' : ''}
                  </Label>
                  <div className="max-h-28 overflow-auto rounded-md border border-border/50 bg-muted/10 p-2 space-y-1.5">
                    {filteredTags.length === 0 ? (
                      <p className="text-xs text-muted-foreground px-1 py-1">
                        {selectedSection === 'all' ? 'No tags available' : 'No tags in this section'}
                      </p>
                    ) : (
                      filteredTags.map((tag: any) => {
                        const tagId = String(tag.id);
                        const checked = selectedTagIds.includes(tagId);
                        return (
                          <label key={tagId} className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const isChecked = e.target.checked;
                                setSelectedTagIds((previous) => (
                                  isChecked
                                    ? (previous.includes(tagId) ? previous : [...previous, tagId])
                                    : previous.filter((id) => id !== tagId)
                                ));
                              }}
                            />
                            <span className="truncate">{tag.name}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    {selectedTagIds.length > 0
                      ? `${selectedTagIds.length} tag(s) selected`
                      : (selectedSection === 'all' ? 'No tag selected: all tags will be used' : 'No tag selected: all tags in selected section will be used')}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-2 col-span-1 lg:col-span-2">
                <Label className="text-xs uppercase font-bold text-muted-foreground">Energy Meter</Label>
                <select
                  className="w-full flex h-9 w-full rounded-md border border-input bg-background text-foreground [color-scheme:dark] px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={selectedMeter}
                  onChange={(e) => setSelectedMeter(e.target.value)}
                >
                  <option value="">Select Meter...</option>
                  {meters.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                <div className="mt-2 border border-border/50 rounded-md p-2.5 bg-muted/10 space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <Label className="text-[10px] uppercase font-bold text-muted-foreground">Export Meters</Label>
                    <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
                      <input
                        type="checkbox"
                        checked={exportAllMeters}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setExportAllMeters(checked);
                          if (!checked) {
                            setSelectedExportMeterIds((prev) => {
                              if (prev.length > 0) return prev;
                              if (selectedMeter) return [selectedMeter];
                              return meters[0]?.id ? [String(meters[0].id)] : [];
                            });
                          }
                        }}
                      />
                      All Meters
                    </label>
                  </div>

                  {!exportAllMeters && (
                    <div className="max-h-28 overflow-auto grid grid-cols-1 sm:grid-cols-2 gap-1.5 pr-1">
                      {meters.map((meter) => (
                        <label key={String(meter.id)} className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedExportMeterIds.includes(String(meter.id))}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const meterId = String(meter.id);
                              setSelectedExportMeterIds((prev) => (
                                checked
                                  ? (prev.includes(meterId) ? prev : [...prev, meterId])
                                  : prev.filter((id) => id !== meterId)
                              ));
                            }}
                          />
                          <span className="truncate">{meter.name}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  <div className="pt-1 border-t border-border/30">
                    <Label className="text-[10px] uppercase font-bold text-muted-foreground">Export Meter Tags (Optional)</Label>
                    <div className="mt-1 max-h-32 overflow-auto rounded-md border border-border/40 bg-background/70 p-2 space-y-1.5">
                      {energyMeterTagOptions.length === 0 ? (
                        <p className="text-[10px] text-muted-foreground">
                          No tag list found for selected meter(s). All available tags from readings will be exported.
                        </p>
                      ) : (
                        energyMeterTagOptions.map((option) => {
                          const checked = selectedMeterTagKeys.includes(option.key);
                          return (
                            <label key={option.key} className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  setSelectedMeterTagKeys((prev) => (
                                    isChecked
                                      ? (prev.includes(option.key) ? prev : [...prev, option.key])
                                      : prev.filter((key) => key !== option.key)
                                  ));
                                }}
                              />
                              <span className="truncate">{option.meterName} - {option.tagName}{option.unit ? ` (${option.unit})` : ''}</span>
                            </label>
                          );
                        })
                      )}
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {selectedMeterTagKeys.length > 0
                        ? `${selectedMeterTagKeys.length} tag(s) selected for export.`
                        : 'No tag selected: all tags from selected meter(s) will be exported.'}
                    </p>
                  </div>

                  <p className="text-[10px] text-muted-foreground">
                    {exportAllMeters
                      ? `All available meters will be exported (${meters.length}).`
                      : `${selectedExportMeterIds.length} meter(s) selected for export.`}
                  </p>
                </div>
              </div>
            )}

            <div className="col-span-1 lg:col-span-2 space-y-2 rounded-md border border-border/40 bg-muted/10 p-2.5">
              <div className="grid grid-cols-1 sm:grid-cols-[130px_1fr_auto] gap-2 items-end">
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase font-bold text-muted-foreground">Report Interval</Label>
                  <Input
                    type="number"
                    min={intervalUnit === 'seconds' ? String(MIN_REPORT_SECONDS_INTERVAL) : '0'}
                    step="1"
                    value={intervalValue}
                    onChange={(e) => {
                      setIntervalCustomized(true);
                      setIntervalValue(normalizeIntervalValue(Number(e.target.value) || 0, intervalUnit));
                    }}
                    className="h-8"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase font-bold text-muted-foreground">Unit</Label>
                  <select
                    className="w-full h-8 rounded-md border border-input bg-background text-foreground [color-scheme:dark] px-3 py-1 text-xs shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    value={intervalUnit}
                    onChange={(e) => {
                      setIntervalCustomized(true);
                      const nextUnit = e.target.value as IntervalUnit;
                      setIntervalUnit(nextUnit);
                      setIntervalValue((prev) => normalizeIntervalValue(prev, nextUnit));
                    }}
                    disabled={intervalValue <= 0}
                  >
                    <option value="seconds">Seconds</option>
                    <option value="minutes">Minutes</option>
                    <option value="hours">Hours</option>
                  </select>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="h-8 px-2 text-xs"
                  onClick={() => {
                    const parsed = parseIntervalParam(adminDefaultInterval || '0');
                    setIntervalCustomized(false);
                    setIntervalValue(parsed.value);
                    setIntervalUnit(parsed.unit);
                  }}
                >
                  Use Admin Default
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground">
                Active: <strong>{formatIntervalLabel(intervalParam)}</strong> · Admin default: <strong>{formatIntervalLabel(adminDefaultInterval || '0')}</strong>
              </p>
              <p className="text-[10px] text-muted-foreground">Set value to 0 for raw data (no grouping).</p>
              <p className="text-[10px] text-muted-foreground">When unit is seconds, minimum allowed is {MIN_REPORT_SECONDS_INTERVAL}s.</p>
            </div>

            {/* Range Selector */}
            <div className="space-y-2 col-span-1 lg:col-span-1">
              <Label className="text-xs uppercase font-bold text-muted-foreground">Quick Presets</Label>
              <select
                className="w-full flex h-9 w-full rounded-md border border-input bg-background text-foreground [color-scheme:dark] px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={dateRange}
                onChange={(e) => applyPreset(e.target.value)}
              >
                <option value="custom">— Manual —</option>
                <option value="today">Today</option>
                <option value="1d">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="6m">Last 6 Months</option>
                <option value="1y">Last 1 Year</option>
              </select>
            </div>

          </div>

          {/* Always-visible Date & Time Filter Row */}
          <div className="border border-border/40 rounded-xl p-4 bg-muted/10 space-y-3">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" /> Date &amp; Time Filter
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-muted-foreground">Start Date</Label>
                <Input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-muted-foreground">Start Time</Label>
                <Input type="time" value={customStartTime} onChange={e => setCustomStartTime(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-muted-foreground">End Date</Label>
                <Input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-muted-foreground">End Time</Label>
                <Input type="time" value={customEndTime} onChange={e => setCustomEndTime(e.target.value)} />
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Showing data from <strong>{customStart} {customStartTime}</strong> → <strong>{customEnd} {customEndTime}</strong>
            </p>
          </div>
          
          <div className="flex flex-wrap items-center justify-end gap-3 pt-4 border-t border-border/40">
            <Button
              variant="outline"
              onClick={() => downloadFile('excel')}
              disabled={!canDownload}
              className="gap-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <Download className="h-4 w-4" /> Excel
            </Button>
            <Button
              variant="outline"
              onClick={() => downloadFile('pdf')}
              disabled={!canDownload}
              className="gap-2 border-red-500/50 text-red-400 hover:bg-red-500/10"
            >
              <Download className="h-4 w-4" /> PDF Report
            </Button>
            <Button onClick={fetchData} disabled={loading} className="gap-2 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 shadow-lg shadow-blue-500/20">
              <RefreshCcw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Fetch Data
            </Button>
          </div>
        </CardContent>
      </Card>

      {deviceType === 'summary' && summaryStats && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2 bg-card border-border/60 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Overview (All Meters)</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-0">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Average Voltage</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">{summaryStats.avgVoltage.toFixed(1)} V</p>
                <p className="text-xs text-muted-foreground mt-1">Min: {summaryStats.minVoltage.toFixed(1)} V &nbsp;|&nbsp; Max: {summaryStats.maxVoltage.toFixed(1)} V</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Average Active kW</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">{summaryStats.avgActiveKw.toFixed(1)} kW</p>
                <p className="text-xs text-muted-foreground mt-1">Min: {summaryStats.minActiveKw.toFixed(1)} kW &nbsp;|&nbsp; Max: {summaryStats.maxActiveKw.toFixed(1)} kW</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Total Consumption (kWh)</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">{summaryStats.totalConsumption.toFixed(1)}</p>
                <p className="text-xs text-muted-foreground mt-1">Peak Active Power: {summaryStats.peakKw.toFixed(1)} kW</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border/60 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Meter Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2.5 pt-0 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Total Meters</span>
                <span className="font-semibold text-foreground">{summaryMeterCounts.total}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Active Meters</span>
                <span className="font-semibold text-emerald-500">{summaryMeterCounts.active}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Inactive Meters</span>
                <span className="font-semibold text-muted-foreground">{summaryMeterCounts.inactive}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Chart Visualization */}
      <Card className="bg-card border-border/60 shadow-sm overflow-hidden min-h-[400px]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-500" />
            {deviceType === 'summary' ? 'Energy Meter Trend (All Meters)' : 'Response Curve'}
          </CardTitle>
          <CardDescription>
            {deviceType === 'summary' ? 'Voltage, Active kW and cumulative Energy across the selected period' : 'Visual mapping of historical data points'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {deviceType === 'summary' ? (
            summaryTrendData.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground border-2 border-dashed border-border/50 rounded-xl bg-muted/10">
                <Database className="h-12 w-12 opacity-20 mb-3" />
                <p>No trend data yet. Adjust filters and click "Fetch Data".</p>
              </div>
            ) : (
              <>
                <div className="h-[400px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={summaryTrendData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={30} />
                      {/* Explicit padded domains — without these, Recharts' 'auto' domain can be
                          computed too tightly around the bulk of the data and clip a genuine
                          outlier point (e.g. today's still-partial-day bucket) right off the
                          top of the chart instead of scaling to include it. */}
                      <YAxis yAxisId="voltage" orientation="left" tick={{ fontSize: 10 }} stroke="#3b82f6"
                        domain={[(min: number) => Math.floor(min - Math.max(5, Math.abs(min) * 0.05)), (max: number) => Math.ceil(max + Math.max(5, Math.abs(max) * 0.05))]}
                        label={{ value: 'Voltage (V)', angle: -90, position: 'insideLeft', fontSize: 10, fill: '#3b82f6' }} />
                      <YAxis yAxisId="kw" orientation="right" tick={{ fontSize: 10 }} stroke="#10b981"
                        domain={[0, (max: number) => Math.ceil((max || 1) * 1.1)]}
                        label={{ value: 'Active kW', angle: 90, position: 'insideRight', fontSize: 10, fill: '#10b981' }} />
                      <YAxis yAxisId="kwh" orientation="right" tick={{ fontSize: 10 }} stroke="#f59e0b"
                        domain={[0, (max: number) => Math.ceil((max || 1) * 1.1)]}
                        label={{ value: 'Energy (kWh)', angle: 90, position: 'insideRight', fontSize: 10, fill: '#f59e0b' }} />
                      <Tooltip contentStyle={{ backgroundColor: 'rgba(10, 10, 10, 0.9)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }} />
                      <Legend />
                      <Line yAxisId="voltage" type="monotone" dataKey="avgVoltage" name="Voltage (V)" stroke="#3b82f6" strokeWidth={2} dot={false} activeDot={{ r: 5 }} />
                      <Line yAxisId="kw" type="monotone" dataKey="avgActiveKw" name="Active kW" stroke="#10b981" strokeWidth={2} dot={false} activeDot={{ r: 5 }} />
                      <Line yAxisId="kwh" type="monotone" dataKey="cumulativeKwh" name="Energy (kWh)" stroke="#f59e0b" strokeWidth={2} dot={false} activeDot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Note: Energy (kWh) is cumulative over the selected period.</p>
              </>
            )
          ) : data.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground border-2 border-dashed border-border/50 rounded-xl bg-muted/10">
              <Database className="h-12 w-12 opacity-20 mb-3" />
              <p>No data fetched yet. Adjust filters and click "Fetch Data".</p>
            </div>
          ) : (
            <div className="h-[400px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                {(() => {
                  // Boolean/M-bit tags only ever read 0 or 1 — plotted on the same
                  // shared axis as an analog tag with a much larger range (e.g.
                  // 0-4000), they'd flatline invisibly along the bottom. Give any
                  // tag whose values are all 0/1 its own 0-1 axis instead.
                  const binaryTagNames = new Set(
                    activeTagNames.filter(tagName =>
                      data.length > 0 &&
                      data.every((d: any) => d[tagName] === undefined || d[tagName] === null || d[tagName] === 0 || d[tagName] === 1) &&
                      data.some((d: any) => d[tagName] === 0 || d[tagName] === 1)
                    )
                  );
                  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];
                  return (
                    <LineChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis dataKey="formattedTime" tick={{fontSize: 10}} minTickGap={50} />
                      <YAxis yAxisId="main" />
                      {binaryTagNames.size > 0 && (
                        <YAxis yAxisId="binary" orientation="right" domain={[0, 1]} ticks={[0, 1]} tick={{ fontSize: 10 }} />
                      )}
                      <Tooltip
                        contentStyle={{ backgroundColor: 'rgba(10, 10, 10, 0.9)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}
                      />
                      <Legend />
                      {activeTagNames.map((tagName, i) => (
                        <Line
                          key={tagName}
                          yAxisId={binaryTagNames.has(tagName) ? 'binary' : 'main'}
                          type="monotone"
                          dataKey={tagName}
                          stroke={colors[i % colors.length]}
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 6 }}
                          name={tagName.replace(/_/g, ' ')}
                        />
                      ))}
                    </LineChart>
                  );
                })()}
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Data Grid */}
      {deviceType === 'summary' && summaryTableRows.length > 0 ? (
        <Card className="bg-card border-border/60 shadow-sm overflow-hidden">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              Meter-Wise Summary <span className="text-xs bg-blue-500/10 text-blue-600 px-2 py-0.5 rounded-full">{summaryTableRows.length} meters</span>
            </CardTitle>
          </CardHeader>
          <div className="overflow-x-auto max-h-[440px]">
            <table className="w-full text-sm text-left">
              <thead className="text-xs uppercase bg-muted sticky top-0 z-10 text-muted-foreground font-semibold">
                <tr>
                  <th rowSpan={2} className="px-3 sm:px-6 py-2 border-b align-bottom">Meter Name</th>
                  <th colSpan={3} className="px-3 sm:px-6 py-1.5 border-b text-center">Average Voltage (V)</th>
                  <th colSpan={3} className="px-3 sm:px-6 py-1.5 border-b text-center">Average Active kW</th>
                  <th rowSpan={2} className="px-3 sm:px-6 py-2 border-b align-bottom">Total Consumption (kWh)</th>
                  <th rowSpan={2} className="px-3 sm:px-6 py-2 border-b align-bottom">% of Total</th>
                </tr>
                <tr>
                  <th className="px-3 py-1.5 border-b text-center font-medium normal-case">Average</th>
                  <th className="px-3 py-1.5 border-b text-center font-medium normal-case">Min</th>
                  <th className="px-3 py-1.5 border-b text-center font-medium normal-case">Max</th>
                  <th className="px-3 py-1.5 border-b text-center font-medium normal-case">Average</th>
                  <th className="px-3 py-1.5 border-b text-center font-medium normal-case">Min</th>
                  <th className="px-3 py-1.5 border-b text-center font-medium normal-case">Max</th>
                </tr>
              </thead>
              <tbody>
                {summaryTableRows.map((row, idx) => (
                  <motion.tr
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(idx * 0.05, 1) }}
                    key={row.meterId || idx}
                    className="border-b transition-colors hover:bg-muted/30"
                  >
                    <td className="px-3 sm:px-6 py-3.5 whitespace-nowrap">{row.meterName}</td>
                    <td className="px-3 py-3.5 text-center whitespace-nowrap">{row.avgVoltage.toFixed(2)}</td>
                    <td className="px-3 py-3.5 text-center whitespace-nowrap">{row.minVoltage.toFixed(2)}</td>
                    <td className="px-3 py-3.5 text-center whitespace-nowrap">{row.maxVoltage.toFixed(2)}</td>
                    <td className="px-3 py-3.5 text-center whitespace-nowrap">{row.avgActiveKw.toFixed(2)}</td>
                    <td className="px-3 py-3.5 text-center whitespace-nowrap">{row.minActiveKw.toFixed(2)}</td>
                    <td className="px-3 py-3.5 text-center whitespace-nowrap">{row.maxActiveKw.toFixed(2)}</td>
                    <td className="px-3 sm:px-6 py-3.5 whitespace-nowrap">{row.totalConsumption.toFixed(2)}</td>
                    <td className="px-3 sm:px-6 py-3.5 whitespace-nowrap">{row.percentOfTotal.toFixed(2)}%</td>
                  </motion.tr>
                ))}
              </tbody>
              {summaryStats && (
                <tfoot>
                  <tr className="bg-muted/60 font-semibold text-foreground border-t-2 border-border">
                    <td className="px-3 sm:px-6 py-3 whitespace-nowrap">TOTAL / AVERAGE</td>
                    <td className="px-3 py-3 text-center whitespace-nowrap">{summaryStats.avgVoltage.toFixed(2)}</td>
                    <td className="px-3 py-3 text-center whitespace-nowrap">{summaryStats.minVoltage.toFixed(2)}</td>
                    <td className="px-3 py-3 text-center whitespace-nowrap">{summaryStats.maxVoltage.toFixed(2)}</td>
                    <td className="px-3 py-3 text-center whitespace-nowrap">{summaryStats.avgActiveKw.toFixed(2)}</td>
                    <td className="px-3 py-3 text-center whitespace-nowrap">{summaryStats.minActiveKw.toFixed(2)}</td>
                    <td className="px-3 py-3 text-center whitespace-nowrap">{summaryStats.maxActiveKw.toFixed(2)}</td>
                    <td className="px-3 sm:px-6 py-3 whitespace-nowrap">{summaryStats.totalConsumption.toFixed(2)}</td>
                    <td className="px-3 sm:px-6 py-3 whitespace-nowrap">100.00%</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Card>
      ) : data.length > 0 ? (
        <Card className="bg-card border-border/60 shadow-sm overflow-hidden">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              Data Grid View <span className="text-xs bg-blue-500/10 text-blue-600 px-2 py-0.5 rounded-full">{data.length} records</span>
            </CardTitle>
          </CardHeader>
          <div className="overflow-x-auto max-h-[400px]">
            <table className="w-full text-sm text-left">
              <thead className="text-xs uppercase bg-muted sticky top-0 z-10 text-muted-foreground font-semibold">
                <tr>
                  <th className="px-3 sm:px-6 py-3 border-b">Timestamp</th>
                  {deviceType === 'plc' && <th className="px-3 sm:px-6 py-3 border-b">Tags Found</th>}
                  {deviceType === 'energy_meter' && <th className="px-3 sm:px-6 py-3 border-b">Register ID</th>}
                  <th className="px-3 sm:px-6 py-3 border-b">Values</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, idx) => (
                  <motion.tr 
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(idx * 0.05, 1) }}
                    key={row.id || idx} 
                    className="border-b transition-colors hover:bg-muted/30"
                  >
                    <td className="px-3 sm:px-6 py-3.5 whitespace-nowrap">{row.formattedTime}</td>
                    {deviceType === 'plc' ? (
                      <td className="px-3 sm:px-6 py-3.5 text-muted-foreground whitespace-normal break-words sm:whitespace-nowrap">{activeTagNames.join(', ')}</td>
                    ) : (
                      <td className="px-3 sm:px-6 py-3.5 whitespace-nowrap text-muted-foreground">{row.tag_id}</td>
                    )}
                    <td className="px-3 sm:px-6 py-3.5 font-medium text-foreground whitespace-normal break-words sm:whitespace-nowrap">
                      {activeTagNames.map(tagName => `${tagName}: ${row[tagName] || 'N/A'}`).join(' | ')}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}

    </div>
  );
}
